import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import {
  ShieldCheck, ListTree, ScrollText, SlidersHorizontal, Lock,
  RefreshCw, Play, PauseCircle, PlayCircle, LogOut, ArrowLeft, TrendingUp,
  Handshake, UserSquare2, Database, List, LayoutGrid, CalendarDays, Link2, Unlink,
  ArrowUp, ArrowDown, Plus, Trash2, Mail, AlertTriangle, HeartPulse, Wrench, Cable, Search, X,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/command-center`;

// Shared search box for Command Center lists (Jobs / Incidents / Integrations / Customers).
function CcSearch({ value, onChange, placeholder, testId, count }) {
  return (
    <div className="flex items-center gap-2">
      <div className="relative w-full max-w-md">
        <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
        <Input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
               className="pl-8 pr-8" data-testid={testId} />
        {value && (
          <button type="button" onClick={() => onChange('')} aria-label="Clear search" data-testid={`${testId}-clear`}
                  className="absolute right-2 top-2.5 text-muted-foreground hover:text-foreground">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      {count !== undefined && <span className="text-xs text-muted-foreground whitespace-nowrap" data-testid={`${testId}-count`}>{count} match{count === 1 ? '' : 'es'}</span>}
    </div>
  );
}

function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

// Spec Section 14.1 — exactly these 7 top-level items, in this order.
// "capability" gates visibility; items with no real panel yet render ghosted.
const NAV_ITEMS = [
  { key: 'home', label: 'Home', icon: ShieldCheck, capability: 'home', ready: true },
  { key: 'sales', label: 'Sales', icon: Handshake, capability: 'leads_view', ready: true },
  { key: 'customers', label: 'Customers', icon: UserSquare2, capability: 'customer_360_view', ready: true },
  { key: 'operations', label: 'Operations', icon: ListTree, capability: 'jobs_view', ready: true },
  { key: 'analytics', label: 'Analytics', icon: TrendingUp, capability: 'home', ready: true },
  { key: 'audit', label: 'Audit Log', icon: ScrollText, capability: 'audit_log_view', ready: true },
  { key: 'settings', label: 'Settings', icon: SlidersHorizontal, capability: 'admin_users_manage', ready: true, altCapability: 'config_thresholds', altCapability2: 'leads_edit' },
];

const ROLE_LABELS = {
  owner: 'Owner', ops_admin: 'Ops Admin', sales_admin: 'Sales Admin',
  customer_success: 'Customer Success', read_only: 'Read Only',
};

export default function CommandCenterPage({ user, onLogout }) {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).has('calendar') ? 'settings' : 'home');

  const loadMe = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/me`);
      setMe(res.data);
    } catch (error) {
      if (error.response?.status === 403 || error.response?.status === 401) setDenied(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadMe(); }, [loadMe]);

  if (loading) {
    return (
      <div className="dark min-h-screen bg-background flex items-center justify-center" data-testid="command-center-loading">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (denied || !me) {
    return (
      <div className="dark min-h-screen bg-background flex items-center justify-center p-6" data-testid="command-center-denied">
        <Card className="max-w-md w-full border-destructive/40">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive"><Lock className="h-5 w-5" /> Access Denied</CardTitle>
            <CardDescription>This area is restricted to PAC internal admin staff.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button data-testid="command-center-back-btn" onClick={() => navigate('/')} className="w-full">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to app
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const caps = new Set(me.capabilities);
  const mfaEligible = ['owner', 'ops_admin'].includes(me.user?.admin_role);
  const visibleNav = NAV_ITEMS.filter((n) => caps.has(n.capability) || (n.altCapability && caps.has(n.altCapability)) || (n.altCapability2 && caps.has(n.altCapability2))
    || (n.key === 'settings' && mfaEligible));

  return (
    <div className="dark min-h-screen bg-background text-foreground flex overflow-x-hidden" data-testid="command-center-shell">
      <aside className="w-48 sm:w-64 border-r border-border flex flex-col shrink-0">
        <div className="p-5 border-b border-border">
          <p className="text-xs tracking-widest text-muted-foreground font-mono">PAC</p>
          <h1 className="text-lg font-semibold tracking-tight">Command Center</h1>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {visibleNav.map((item) => {
            const Icon = item.icon;
            return (
              <button
                key={item.key}
                data-testid={`cc-nav-${item.key}`}
                onClick={() => item.ready && setTab(item.key)}
                disabled={!item.ready}
                className={`w-full flex items-center justify-between gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${
                  !item.ready ? 'text-muted-foreground/40 cursor-not-allowed' :
                  tab === item.key ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                }`}
              >
                <span className="flex items-center gap-2.5"><Icon className="h-4 w-4" /> {item.label}</span>
                {!item.ready && <Badge variant="outline" className="text-[10px] opacity-60">soon</Badge>}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-border space-y-2">
          <div className="px-3 py-2 rounded-md bg-muted/40">
            <p className="text-sm font-medium truncate" data-testid="cc-current-user">{me.user.name || me.user.email}</p>
            <Badge className="mt-1" data-testid="cc-current-role">{ROLE_LABELS[me.user.admin_role] || me.user.admin_role}</Badge>
          </div>
          <Button variant="outline" size="sm" className="w-full justify-start" data-testid="cc-back-to-app-btn" onClick={() => navigate('/')}>
            <ArrowLeft className="h-4 w-4 mr-2" /> Back to app
          </Button>
          <Button variant="ghost" size="sm" className="w-full justify-start" data-testid="cc-logout-btn" onClick={() => { onLogout(); navigate('/'); }}>
            <LogOut className="h-4 w-4 mr-2" /> Log out
          </Button>
        </div>
      </aside>

      <main className="flex-1 min-w-0 p-4 sm:p-8 overflow-y-auto overflow-x-hidden">
        {tab === 'home' && <HomePanel me={me} />}
        {tab === 'sales' && caps.has('leads_view') && <SalesPanel canEdit={caps.has('leads_edit')} />}
        {tab === 'customers' && caps.has('customer_360_view') && (
          <CustomersPanel canManage={caps.has('tickets_manage')} canSeeCS={caps.has('cs_queue_view')} canManageCS={caps.has('cs_queue_manage')} />
        )}
        {tab === 'operations' && caps.has('jobs_view') && (
          <OperationsPanel canManage={caps.has('jobs_manage')} canIncidents={caps.has('incidents_view')} canResolveIncidents={caps.has('incidents_manage')}
                            canMergeRecords={caps.has('merge_records')} canMergeAccounts={caps.has('merge_accounts')} />
        )}
        {tab === 'audit' && caps.has('audit_log_view') && <AuditLogPanel />}
        {tab === 'analytics' && caps.has('home') && <AnalyticsPanel />}
        {tab === 'settings' && (caps.has('admin_users_manage') || caps.has('config_thresholds') || caps.has('leads_edit') || mfaEligible) && (
          <SettingsGroupPanel canManageUsers={caps.has('admin_users_manage')} canManageConfig={caps.has('config_thresholds')} canManageCalendar={caps.has('leads_edit')}
                              mfaEligible={mfaEligible} />
        )}
      </main>
    </div>
  );
}

function OperationsPanel({ canManage, canIncidents, canResolveIncidents, canMergeRecords, canMergeAccounts }) {
  const [subTab, setSubTab] = useState('jobs');
  const OPS_SUBTABS = [
    { key: 'jobs', label: 'Jobs', ready: true },
    { key: 'incidents', label: 'Incidents', ready: canIncidents },
    { key: 'integrations', label: 'Integrations', ready: true },
    { key: 'health_check', label: 'Health Check', ready: true },
    { key: 'data_integrity', label: 'Data Integrity', ready: canMergeRecords },
    { key: 'platforms', label: 'Platforms', ready: true },
  ];
  return (
    <div className="space-y-6" data-testid="cc-operations-panel">
      <div>
        <h2 className="text-2xl font-semibold">Operations</h2>
        <p className="text-muted-foreground text-sm mt-1">Jobs, incidents, integrations, health check, data integrity.</p>
      </div>
      <div className="flex gap-1 border-b border-border">
        {OPS_SUBTABS.map((s) => (
          <button key={s.key} disabled={!s.ready} data-testid={`cc-ops-subtab-${s.key}`}
                  onClick={() => s.ready && setSubTab(s.key)}
                  className={`px-3 py-2 text-sm border-b-2 -mb-px ${!s.ready ? 'text-muted-foreground/40 cursor-not-allowed' :
                    subTab === s.key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {s.label}{!s.ready && <span className="ml-1.5 text-[10px] opacity-60">(no access)</span>}
          </button>
        ))}
      </div>
      {subTab === 'jobs' && <JobsPanel canManage={canManage} />}
      {subTab === 'incidents' && canIncidents && <IncidentsPanel canManage={canResolveIncidents} />}
      {subTab === 'integrations' && <IntegrationHealthPanel />}
      {subTab === 'health_check' && <HealthCheckPanel />}
      {subTab === 'data_integrity' && canMergeRecords && <DataIntegrityPanel canMergeAccounts={canMergeAccounts} />}
      {subTab === 'platforms' && <PlatformPanel />}
    </div>
  );
}

const STATUS_DOT_COLORS = { green: 'bg-emerald-500', yellow: 'bg-amber-500', red: 'bg-red-500', no_data: 'bg-muted-foreground/30' };
const PLATFORM_LABELS = { web: 'Web', android: 'Android', ios: 'iOS', unknown: 'Unknown' };

function PlatformPanel() {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios.get(`${API}/platforms`).then((r) => setData(r.data))
      .catch(() => toast.error('Failed to load platform panel'));
  }, []);
  if (!data) return <p className="text-muted-foreground text-sm">Loading…</p>;
  return (
    <div className="space-y-4" data-testid="cc-platform-panel">
      <div>
        <h3 className="text-lg font-semibold">Platform Panel</h3>
        <p className="text-muted-foreground text-sm mt-1">
          Per-platform active users, sessions, versions, error rate and p95 latency. iOS shows "no data yet" until that app ships.
          {data.unknown_share_pct > 0 && ` ${data.unknown_share_pct}% of sessions are missing platform headers.`}
        </p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {['web', 'android', 'ios', 'unknown'].map((p) => {
          const m = data[p];
          return (
            <Card key={p} data-testid={`cc-platform-card-${p}`}>
              <CardContent className="pt-6 space-y-2">
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{PLATFORM_LABELS[p]}</span>
                  <span className={`h-2.5 w-2.5 rounded-full ${STATUS_DOT_COLORS[m.status_dot]}`} data-testid={`cc-platform-status-dot-${p}`} />
                </div>
                {m.no_data_yet ? (
                  <p className="text-muted-foreground text-sm">No data yet</p>
                ) : (
                  <div className="text-xs space-y-1 text-muted-foreground">
                    <p>Active users (7d): <span className="text-foreground font-medium">{m.active_users_7d}</span></p>
                    <p>Sessions: <span className="text-foreground font-medium">{m.sessions}</span></p>
                    <p>Error rate (7d): <span className="text-foreground font-medium">{m.error_rate_7d != null ? `${(m.error_rate_7d * 100).toFixed(1)}%` : '—'}</span></p>
                    <p>p95 latency: <span className="text-foreground font-medium">{m.p95_latency_ms != null ? `${m.p95_latency_ms}ms` : '—'}</span></p>
                    <p>Versions: <span className="text-foreground font-medium">{Object.keys(m.versions).length ? Object.entries(m.versions).map(([v, c]) => `${v} (${c})`).join(', ') : '—'}</span></p>
                    {m.unknown_version_share_pct != null && <p data-testid={`cc-platform-unknown-version-${p}`}>Unknown-version share: <span className="text-foreground font-medium">{m.unknown_version_share_pct}%</span></p>}
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

const SEVERITY_COLORS = { P1: 'destructive', P2: 'destructive', P3: 'secondary', P4: 'outline' };
const INCIDENT_STATUS_VARIANT = { OPEN: 'secondary', MONITORING: 'outline', RESOLVED: 'default' };
const IMPACT_LABELS = { INTERNAL: 'Internal', POTENTIAL_CUSTOMER_IMPACT: 'Potential customer impact', CUSTOMER_AFFECTED: 'Customer affected' };

function IncidentsPanel({ canManage }) {
  const [incidents, setIncidents] = useState(null);
  const [statusFilter, setStatusFilter] = useState('OPEN');
  const [resolvingId, setResolvingId] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [search, setSearch] = useState('');
  const q = useDebounced(search);

  const load = useCallback(() => {
    const params = statusFilter !== 'all' ? { status: statusFilter } : {};
    if (q.trim()) params.q = q.trim();
    axios.get(`${API}/incidents`, { params }).then((r) => setIncidents(r.data.incidents || []))
      .catch(() => toast.error('Failed to load incidents'));
  }, [statusFilter, q]);
  useEffect(() => { load(); }, [load]);

  const resolve = async (id) => {
    if (!resolutionNotes.trim()) { toast.error('Add resolution notes first'); return; }
    try {
      await axios.post(`${API}/incidents/${id}/resolve`, { resolution_notes: resolutionNotes.trim() });
      toast.success('Incident resolved — owner will get a one-time resolved alert if it was ever escalated');
      setResolvingId(null); setResolutionNotes(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not resolve'); }
  };

  const fmt = (iso) => iso ? iso.slice(0, 19).replace('T', ' ') : '—';

  return (
    <div className="space-y-4" data-testid="cc-incidents-panel">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Incidents</h3>
        <p className="text-muted-foreground text-sm mt-1">Deduped by fingerprint — repeats update occurrences, never duplicate. A cleared signal goes OPEN→MONITORING→RESOLVED after a quiet period (or an admin resolves it any time). P1/P2 alert the owner (email+SMS for P1).</p>
      </div>
      <div className="flex gap-1 border-b border-border">
        {['all', 'OPEN', 'MONITORING', 'RESOLVED'].map((s) => (
          <button key={s} data-testid={`cc-incidents-filter-${s}`} onClick={() => setStatusFilter(s)}
                  className={`px-3 py-2 text-sm border-b-2 -mb-px ${statusFilter === s ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {s === 'all' ? 'All' : s}
          </button>
        ))}
      </div>
      <CcSearch value={search} onChange={setSearch} testId="cc-incidents-search" count={incidents ? incidents.length : undefined}
                placeholder="Search incident id, summary, component, impact, severity…" />
      <Card>
        <CardContent className="pt-6">
          {!incidents ? (
            <p className="text-muted-foreground text-sm">Loading…</p>
          ) : incidents.length === 0 ? (
            <p className="text-muted-foreground text-sm" data-testid="cc-incidents-empty">{search ? 'No incidents match this search.' : 'No incidents.'}</p>
          ) : (
            <Table data-testid="cc-incidents-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Severity</TableHead><TableHead>Summary</TableHead><TableHead>Impact</TableHead><TableHead>Occurrences</TableHead>
                  <TableHead>First seen</TableHead><TableHead>Last seen</TableHead><TableHead>Status</TableHead>
                  {canManage && <TableHead>Action</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {incidents.map((i) => (
                  <TableRow key={i.id} data-testid={`cc-incident-row-${i.id}`}>
                    <TableCell><Badge variant={SEVERITY_COLORS[i.severity] || 'outline'} data-testid={`cc-incident-severity-${i.id}`}>{i.severity}</Badge></TableCell>
                    <TableCell className="text-xs max-w-sm">
                      {i.summary}{i.is_test_data && <Badge variant="outline" className="ml-1.5 text-[10px]">TEST</Badge>}
                      <br /><span className="text-muted-foreground font-mono">{i.fingerprint}</span>
                    </TableCell>
                    <TableCell className="text-xs" data-testid={`cc-incident-impact-${i.id}`}>
                      {IMPACT_LABELS[i.impact] || i.impact || '—'}
                      {i.affected_accounts_count ? ` (${i.affected_accounts_count})` : ''}
                    </TableCell>
                    <TableCell className="text-xs">{i.occurrences}</TableCell>
                    <TableCell className="text-xs font-mono">{fmt(i.first_seen_at)}</TableCell>
                    <TableCell className="text-xs font-mono">{fmt(i.last_seen_at)}</TableCell>
                    <TableCell><Badge variant={INCIDENT_STATUS_VARIANT[i.status] || 'outline'} data-testid={`cc-incident-status-${i.id}`}>{i.status}</Badge></TableCell>
                    {canManage && (
                      <TableCell>
                        {i.status !== 'RESOLVED' && (
                          resolvingId === i.id ? (
                            <div className="flex gap-1 items-center">
                              <Input value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)}
                                     placeholder="Resolution notes" className="h-8 text-xs w-40"
                                     data-testid={`cc-incident-resolution-input-${i.id}`} />
                              <Button size="sm" className="h-8 text-xs" onClick={() => resolve(i.id)} data-testid={`cc-incident-resolve-confirm-${i.id}`}>Save</Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setResolvingId(i.id)} data-testid={`cc-incident-resolve-btn-${i.id}`}>
                              Resolve
                            </Button>
                          )
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

const HEALTH_STATUS_VARIANT = { '✓': 'default', '⚠': 'secondary', '✗': 'destructive' };
const HEALTH_COMPONENT_LABELS = {
  database: 'Database', auth: 'Auth', email: 'Email', drive: 'Drive', calendar: 'Calendar',
  sms: 'SMS', job_worker: 'Job Worker', oldest_pending_job: 'Oldest Pending Job',
  ai_provider: 'AI Provider', onboarding: 'Onboarding',
};

function HealthCheckPanel() {
  const [running, setRunning] = useState(false);
  const [latest, setLatest] = useState(null);
  const [history, setHistory] = useState([]);

  const loadHistory = useCallback(() => {
    axios.get(`${API}/health-check/history`).then((r) => {
      setHistory(r.data.history || []);
      if (r.data.history?.length) setLatest(r.data.history[0]);
    }).catch(() => toast.error('Failed to load health check history'));
  }, []);
  useEffect(() => { loadHistory(); }, [loadHistory]);

  const run = async () => {
    setRunning(true);
    try {
      const res = await axios.post(`${API}/health-check/run`);
      setLatest(res.data);
      toast.success(`Health check complete — overall ${res.data.overall}`);
      loadHistory();
    } catch (e) { toast.error(e.response?.data?.detail || 'Health check failed'); } finally { setRunning(false); }
  };

  return (
    <div className="space-y-4" data-testid="cc-health-check-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2"><HeartPulse className="h-4 w-4" /> Health Check</h3>
          <p className="text-muted-foreground text-sm mt-1">Detailed admin check across every component. ✗ raises a P1 incident; recovery to ✓ auto-resolves it.</p>
        </div>
        <Button data-testid="cc-health-check-run-btn" onClick={run} disabled={running}>
          <RefreshCw className={`h-4 w-4 mr-2 ${running ? 'animate-spin' : ''}`} /> {running ? 'Running…' : 'Run health check'}
        </Button>
      </div>
      {latest && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              Overall: <Badge variant={HEALTH_STATUS_VARIANT[latest.overall]} data-testid="cc-health-overall-badge">{latest.overall}</Badge>
              <span className="text-xs text-muted-foreground font-normal ml-2">as of {latest.at?.slice(0, 19).replace('T', ' ')}</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.entries(latest.components || {}).map(([name, c]) => (
              <div key={name} className="flex items-start gap-2 border border-border rounded-md p-2.5" data-testid={`cc-health-component-${name}`}>
                <Badge variant={HEALTH_STATUS_VARIANT[c.status]} className="mt-0.5">{c.status}</Badge>
                <div>
                  <p className="text-sm font-medium">{HEALTH_COMPONENT_LABELS[name] || name}</p>
                  <p className="text-xs text-muted-foreground">{c.detail}</p>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-sm">Run history</CardTitle></CardHeader>
        <CardContent>
          <Table data-testid="cc-health-history-table">
            <TableHeader><TableRow><TableHead>At</TableHead><TableHead>Overall</TableHead></TableRow></TableHeader>
            <TableBody>
              {history.map((h) => (
                <TableRow key={h.id} data-testid={`cc-health-history-row-${h.id}`}>
                  <TableCell className="text-xs font-mono">{h.at?.slice(0, 19).replace('T', ' ')}</TableCell>
                  <TableCell><Badge variant={HEALTH_STATUS_VARIANT[h.overall]}>{h.overall}</Badge></TableCell>
                </TableRow>
              ))}
              {history.length === 0 && <TableRow><TableCell colSpan={2} className="text-center text-muted-foreground py-6 text-xs">No runs yet.</TableCell></TableRow>}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function IntegrationHealthPanel() {
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  useEffect(() => {
    axios.get(`${API}/integration-health`).then((r) => setData(r.data)).catch(() => toast.error('Failed to load integration health'));
  }, []);

  const LABELS = { email: 'Email (Resend)', drive: 'Google Drive', calendar: 'Google Calendar', sms: 'SMS (Quo)', ai_provider: 'AI Provider' };
  const needle = search.trim().toLowerCase();
  const entries = Object.entries(data?.integrations || {}).filter(([key, c]) =>
    !needle || [key, LABELS[key], c.status, c.detail].some((v) => String(v || '').toLowerCase().includes(needle)));

  return (
    <div className="space-y-4" data-testid="cc-integration-health-panel">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2"><Cable className="h-4 w-4" /> Integration Health</h3>
        <p className="text-muted-foreground text-sm mt-1">
          Snapshot from the latest health check{data?.as_of ? ` (${data.as_of.slice(0, 19).replace('T', ' ')})` : ''}. Toggle simulated outages in
          Settings → Config → Failure injection to see these flip live.
        </p>
      </div>
      <CcSearch value={search} onChange={setSearch} testId="cc-integrations-search" count={data ? entries.length : undefined}
                placeholder="Search integration name, status (OK / DEGRADED / DOWN) or detail…" />
      {!data ? (
        <p className="text-muted-foreground text-sm">Loading…</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {entries.map(([key, c]) => (
            <Card key={key} data-testid={`cc-integration-card-${key}`}>
              <CardContent className="pt-6 flex items-start gap-3">
                <Badge variant={HEALTH_STATUS_VARIANT[c.status]} className="mt-0.5">{c.status}</Badge>
                <div>
                  <p className="text-sm font-medium">{LABELS[key] || key}</p>
                  <p className="text-xs text-muted-foreground">{c.detail}</p>
                </div>
              </CardContent>
            </Card>
          ))}
          {Object.keys(data.integrations || {}).length === 0 && (
            <p className="text-muted-foreground text-sm">No health check has run yet — run one from the Health Check tab.</p>
          )}
          {Object.keys(data.integrations || {}).length > 0 && entries.length === 0 && (
            <p className="text-muted-foreground text-sm" data-testid="cc-integrations-empty">No integrations match this search.</p>
          )}
        </div>
      )}
    </div>
  );
}

function DataIntegrityPanel({ canMergeAccounts }) {
  const [issues, setIssues] = useState(null);
  const [excludedTestData, setExcludedTestData] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [reason, setReason] = useState('');
  const [linkInputs, setLinkInputs] = useState({});
  const [mergePicks, setMergePicks] = useState({});
  const [confirmIds, setConfirmIds] = useState({});

  const scan = useCallback(async () => {
    setScanning(true);
    try {
      const res = await axios.get(`${API}/data-integrity/scan`);
      setIssues(res.data.categories);
      setExcludedTestData(res.data.excluded_test_data);
    } catch (e) { toast.error(e.response?.data?.detail || 'Scan failed'); } finally { setScanning(false); }
  }, []);
  useEffect(() => { scan(); }, [scan]);

  const exportCsv = async () => {
    try {
      const res = await axios.get(`${API}/data-integrity/export.csv`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement('a'), { href: url, download: 'data_integrity_export.csv' });
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.response?.data?.detail || 'Export failed'); }
  };

  const repair = async (action, params, confirmId) => {
    if (!reason.trim()) { toast.error('Enter a reason above before repairing'); return; }
    try {
      await axios.post(`${API}/data-integrity/repair`, { action, reason: reason.trim(), params, confirm_id: confirmId });
      toast.success(`${action} applied — before/after audited`);
      scan();
    } catch (e) { toast.error(e.response?.data?.detail || 'Repair failed'); }
  };

  const total = issues ? Object.values(issues).reduce((n, v) => n + v.length, 0) : 0;

  return (
    <div className="space-y-4" data-testid="cc-data-integrity-panel">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2"><Wrench className="h-4 w-4" /> Data Integrity</h3>
          <p className="text-muted-foreground text-sm mt-1">Every repair requires a reason and is written to the audit log with before/after. Test/internal records are excluded from these counts.</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" data-testid="cc-integrity-export-csv-btn" onClick={exportCsv}>Export CSV</Button>
          <Button variant="secondary" data-testid="cc-integrity-scan-btn" onClick={scan} disabled={scanning}>
            <RefreshCw className={`h-4 w-4 mr-2 ${scanning ? 'animate-spin' : ''}`} /> Re-scan
          </Button>
        </div>
      </div>
      {excludedTestData && Object.values(excludedTestData).some((n) => n > 0) && (
        <p className="text-xs text-muted-foreground" data-testid="cc-integrity-excluded-note">
          Excluded from counts above (is_test_data/is_internal): {Object.entries(excludedTestData).filter(([, n]) => n > 0).map(([k, n]) => `${k}: ${n}`).join(', ')}
        </p>
      )}

      <Card>
        <CardContent className="pt-6">
          <Label className="text-xs">Reason (required for any repair below)</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Why is this repair being applied?"
                 className="mt-1" data-testid="cc-integrity-reason-input" />
        </CardContent>
      </Card>

      {!issues ? (
        <p className="text-muted-foreground text-sm">Scanning…</p>
      ) : total === 0 ? (
        <p className="text-muted-foreground text-sm" data-testid="cc-integrity-clean">No integrity issues found.</p>
      ) : (
        <div className="space-y-4">
          {issues.missing_onboarding_progress.length > 0 && (
            <Card data-testid="cc-integrity-missing-onboarding">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Missing onboarding progress ({issues.missing_onboarding_progress.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {issues.missing_onboarding_progress.map((accId) => (
                  <div key={accId} className="flex items-center justify-between text-xs" data-testid={`cc-integrity-row-${accId}`}>
                    <span className="font-mono">{accId}</span>
                    <Button size="sm" variant="outline" onClick={() => repair('recreate_missing_onboarding_progress', { account_id: accId })}
                            data-testid={`cc-integrity-fix-onboarding-${accId}`}>Recreate progress</Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {issues.orphaned_lead_account_link.length > 0 && (
            <Card data-testid="cc-integrity-orphaned-links">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Orphaned lead→account links ({issues.orphaned_lead_account_link.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {issues.orphaned_lead_account_link.map((leadId) => (
                  <div key={leadId} className="flex items-center gap-2 text-xs" data-testid={`cc-integrity-row-${leadId}`}>
                    <span className="font-mono w-48 truncate">{leadId}</span>
                    <Input placeholder="Correct account_id" className="h-8 w-56" data-testid={`cc-integrity-link-input-${leadId}`}
                           value={linkInputs[leadId] || ''} onChange={(e) => setLinkInputs((s) => ({ ...s, [leadId]: e.target.value }))} />
                    <Button size="sm" variant="outline" data-testid={`cc-integrity-fix-link-${leadId}`}
                            onClick={() => repair('link_lead_to_account', { lead_id: leadId, account_id: linkInputs[leadId] })}>
                      Re-link
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {issues.orphaned_running_jobs.length > 0 && (
            <Card data-testid="cc-integrity-orphaned-jobs">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Orphaned RUNNING jobs ({issues.orphaned_running_jobs.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {issues.orphaned_running_jobs.map((jobId) => (
                  <div key={jobId} className="flex items-center justify-between text-xs" data-testid={`cc-integrity-row-${jobId}`}>
                    <span className="font-mono">{jobId}</span>
                    <Button size="sm" variant="outline" onClick={() => repair('resolve_orphaned_job', { job_id: jobId })}
                            data-testid={`cc-integrity-fix-job-${jobId}`}>Requeue</Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {issues.duplicate_leads.length > 0 && (
            <Card data-testid="cc-integrity-duplicate-leads">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Duplicate leads ({issues.duplicate_leads.length})</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {issues.duplicate_leads.map((grp, idx) => {
                  const pick = mergePicks[`lead-${idx}`] || {};
                  const key = `lead-${idx}`;
                  const confirmed = pick.merge && confirmIds[key] === pick.merge;
                  return (
                    <div key={grp.email} className="border border-border rounded-md p-2.5 space-y-2" data-testid={`cc-integrity-dup-lead-group-${idx}`}>
                      <p className="text-xs font-medium">{grp.email}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Select value={pick.keep || ''} onValueChange={(v) => setMergePicks((s) => ({ ...s, [key]: { ...pick, keep: v } }))}>
                          <SelectTrigger className="h-8 text-xs w-40" data-testid={`cc-integrity-keep-lead-${idx}`}><SelectValue placeholder="Keep" /></SelectTrigger>
                          <SelectContent>{grp.lead_ids.map((id) => <SelectItem key={id} value={id}>{id}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={pick.merge || ''} onValueChange={(v) => setMergePicks((s) => ({ ...s, [key]: { ...pick, merge: v } }))}>
                          <SelectTrigger className="h-8 text-xs w-40" data-testid={`cc-integrity-merge-lead-${idx}`}><SelectValue placeholder="Merge (loses)" /></SelectTrigger>
                          <SelectContent>{grp.lead_ids.filter((id) => id !== pick.keep).map((id) => <SelectItem key={id} value={id}>{id}</SelectItem>)}</SelectContent>
                        </Select>
                        {pick.merge && (
                          <Input placeholder={`Type "${pick.merge}" to confirm`} className="h-8 text-xs w-52 font-mono"
                                 data-testid={`cc-integrity-confirm-lead-${idx}`}
                                 value={confirmIds[key] || ''} onChange={(e) => setConfirmIds((s) => ({ ...s, [key]: e.target.value }))} />
                        )}
                        <Button size="sm" variant="destructive" data-testid={`cc-integrity-fix-dup-lead-${idx}`}
                                disabled={!pick.keep || !pick.merge || !confirmed}
                                onClick={() => repair('merge_leads', { keep_lead_id: pick.keep, merge_lead_id: pick.merge }, confirmIds[key])}>
                          Merge leads
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {issues.duplicate_accounts.length > 0 && (
            <Card data-testid="cc-integrity-duplicate-accounts">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Duplicate accounts ({issues.duplicate_accounts.length})</CardTitle>
                {!canMergeAccounts && <CardDescription className="text-[11px]">Owner-only action — you can see these but not merge them.</CardDescription>}
              </CardHeader>
              <CardContent className="space-y-3">
                {issues.duplicate_accounts.map((grp, idx) => {
                  const pick = mergePicks[`acct-${idx}`] || {};
                  const key = `acct-${idx}`;
                  const confirmed = pick.merge && confirmIds[key] === pick.merge;
                  return (
                    <div key={grp.org_name} className="border border-border rounded-md p-2.5 space-y-2" data-testid={`cc-integrity-dup-account-group-${idx}`}>
                      <p className="text-xs font-medium">{grp.org_name}</p>
                      <div className="flex items-center gap-2 flex-wrap">
                        <Select value={pick.keep || ''} onValueChange={(v) => setMergePicks((s) => ({ ...s, [key]: { ...pick, keep: v } }))} disabled={!canMergeAccounts}>
                          <SelectTrigger className="h-8 text-xs w-48" data-testid={`cc-integrity-keep-account-${idx}`}><SelectValue placeholder="Keep" /></SelectTrigger>
                          <SelectContent>{grp.account_ids.map((id) => <SelectItem key={id} value={id}>{id}</SelectItem>)}</SelectContent>
                        </Select>
                        <Select value={pick.merge || ''} onValueChange={(v) => setMergePicks((s) => ({ ...s, [key]: { ...pick, merge: v } }))} disabled={!canMergeAccounts}>
                          <SelectTrigger className="h-8 text-xs w-48" data-testid={`cc-integrity-merge-account-${idx}`}><SelectValue placeholder="Merge (loses)" /></SelectTrigger>
                          <SelectContent>{grp.account_ids.filter((id) => id !== pick.keep).map((id) => <SelectItem key={id} value={id}>{id}</SelectItem>)}</SelectContent>
                        </Select>
                        {canMergeAccounts && pick.merge && (
                          <Input placeholder={`Type "${pick.merge}" to confirm`} className="h-8 text-xs w-52 font-mono"
                                 data-testid={`cc-integrity-confirm-account-${idx}`}
                                 value={confirmIds[key] || ''} onChange={(e) => setConfirmIds((s) => ({ ...s, [key]: e.target.value }))} />
                        )}
                        <Button size="sm" variant="destructive" data-testid={`cc-integrity-fix-dup-account-${idx}`}
                                disabled={!canMergeAccounts || !pick.keep || !pick.merge || !confirmed}
                                onClick={() => repair('merge_accounts', { keep_account_id: pick.keep, merge_account_id: pick.merge }, confirmIds[key])}>
                          Merge accounts
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {issues.user_without_account.length > 0 && (
            <Card data-testid="cc-integrity-user-without-account">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Users without an Account ({issues.user_without_account.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {issues.user_without_account.map((userId) => (
                  <div key={userId} className="flex items-center justify-between text-xs" data-testid={`cc-integrity-row-${userId}`}>
                    <span className="font-mono">{userId}</span>
                    <Button size="sm" variant="outline" onClick={() => repair('backfill_user_account', { user_id: userId })}
                            data-testid={`cc-integrity-fix-user-${userId}`}>Create Account</Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {issues.converted_lead_without_account_link.length > 0 && (
            <Card data-testid="cc-integrity-converted-lead-no-link">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Converted leads without an account link ({issues.converted_lead_without_account_link.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {issues.converted_lead_without_account_link.map((leadId) => (
                  <div key={leadId} className="flex items-center gap-2 text-xs" data-testid={`cc-integrity-row-${leadId}`}>
                    <span className="font-mono w-48 truncate">{leadId}</span>
                    <Input placeholder="Correct account_id" className="h-8 w-56" data-testid={`cc-integrity-converted-link-input-${leadId}`}
                           value={linkInputs[`cv-${leadId}`] || ''} onChange={(e) => setLinkInputs((s) => ({ ...s, [`cv-${leadId}`]: e.target.value }))} />
                    <Button size="sm" variant="outline" data-testid={`cc-integrity-fix-converted-${leadId}`}
                            onClick={() => repair('link_lead_to_account', { lead_id: leadId, account_id: linkInputs[`cv-${leadId}`] })}>
                      Link
                    </Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {issues.account_stage_inconsistent.length > 0 && (
            <Card data-testid="cc-integrity-stage-inconsistent">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Account stage inconsistent with events ({issues.account_stage_inconsistent.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {issues.account_stage_inconsistent.map((accId) => (
                  <div key={accId} className="flex items-center justify-between text-xs" data-testid={`cc-integrity-row-${accId}`}>
                    <span className="font-mono">{accId} — activated_at is set but no account.activated event exists</span>
                    <Button size="sm" variant="outline" onClick={() => repair('correct_lifecycle_stage', { account_id: accId, new_stage: 'ACTIVE' })}
                            data-testid={`cc-integrity-fix-stage-${accId}`}>Correct stage → ACTIVE</Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {issues.lead_missing_required_fields.length > 0 && (
            <Card data-testid="cc-integrity-lead-missing-fields">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Leads missing required fields ({issues.lead_missing_required_fields.length})</CardTitle>
                <CardDescription className="text-[11px]">No automated repair — needs manual review of the source submission.</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {issues.lead_missing_required_fields.map((id) => <span key={id} className="text-xs font-mono border border-border rounded px-2 py-1" data-testid={`cc-integrity-row-${id}`}>{id}</span>)}
              </CardContent>
            </Card>
          )}

          {issues.appointment_without_lead.length > 0 && (
            <Card data-testid="cc-integrity-appointment-no-lead">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Appointments without a Lead ({issues.appointment_without_lead.length})</CardTitle>
                <CardDescription className="text-[11px]">No automated repair — needs manual review.</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {issues.appointment_without_lead.map((id) => <span key={id} className="text-xs font-mono border border-border rounded px-2 py-1" data-testid={`cc-integrity-row-${id}`}>{id}</span>)}
              </CardContent>
            </Card>
          )}

          {issues.drive_rows_without_lead_match.length > 0 && (
            <Card data-testid="cc-integrity-drive-rows">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Drive rows without a matching Lead ID ({issues.drive_rows_without_lead_match.length})</CardTitle>
                <CardDescription className="text-[11px]">External sheet data — no automated repair, review the row directly in Drive.</CardDescription>
              </CardHeader>
              <CardContent className="flex flex-wrap gap-2">
                {issues.drive_rows_without_lead_match.map((id) => <span key={id} className="text-xs font-mono border border-border rounded px-2 py-1" data-testid={`cc-integrity-row-${id}`}>{id}</span>)}
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
  );
}

function SettingsGroupPanel({ canManageUsers, canManageConfig, canManageCalendar, mfaEligible }) {
  const [subTab, setSubTab] = useState(() => new URLSearchParams(window.location.search).has('calendar') && canManageCalendar ? 'my_calendar'
    : canManageUsers ? 'admin_users' : canManageConfig ? 'config' : mfaEligible ? 'security' : 'my_calendar');
  const SETTINGS_SUBTABS = [
    { key: 'admin_users', label: 'Admin Users', ready: canManageUsers },
    { key: 'security', label: 'Security (MFA)', ready: mfaEligible },
    { key: 'config', label: 'Config', ready: canManageConfig },
    { key: 'my_calendar', label: 'My Calendar', ready: canManageCalendar },
    { key: 'lead_statuses', label: 'Lead Statuses', ready: canManageConfig },
    { key: 'templates', label: 'Templates', ready: false },
  ];
  return (
    <div className="space-y-6" data-testid="cc-settings-group-panel">
      <div>
        <h2 className="text-2xl font-semibold">Settings</h2>
        <p className="text-muted-foreground text-sm mt-1">Admin users/roles, config, my calendar, lead statuses, templates.</p>
      </div>
      <div className="flex gap-1 border-b border-border">
        {SETTINGS_SUBTABS.map((s) => (
          <button key={s.key} disabled={!s.ready} data-testid={`cc-settings-subtab-${s.key}`}
                  onClick={() => s.ready && setSubTab(s.key)}
                  className={`px-3 py-2 text-sm border-b-2 -mb-px ${!s.ready ? 'text-muted-foreground/40 cursor-not-allowed' :
                    subTab === s.key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {s.label}{!s.ready && <span className="ml-1.5 text-[10px] opacity-60">(soon)</span>}
          </button>
        ))}
      </div>
      {subTab === 'admin_users' && canManageUsers && <AdminUsersPanel />}
      {subTab === 'security' && mfaEligible && <SecurityMfaPanel />}
      {subTab === 'config' && canManageConfig && <SettingsPanel />}
      {subTab === 'lead_statuses' && canManageConfig && <LeadStatusesPanel />}
      {subTab === 'my_calendar' && canManageCalendar && <MyCalendarPanel />}
    </div>
  );
}

const NEEDS_ATTENTION_ICON = { incident: AlertTriangle, dead_job: Wrench, at_risk_customer: HeartPulse, overdue_follow_up: CalendarDays, overdue_ticket: Mail };
const SYSTEM_STATUS_VARIANT = { HEALTHY: 'default', ATTENTION: 'secondary', CRITICAL: 'destructive' };
const HEALTH_STATE_COLOR = { HEALTHY: 'bg-emerald-500', ATTENTION: 'bg-amber-500', AT_RISK: 'bg-orange-500', CRITICAL: 'bg-red-500', NOT_AVAILABLE: 'bg-muted' };

function HomePanel({ me }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/home`).then((r) => setData(r.data)).catch(() => toast.error('Failed to load Home'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const fmt = (iso) => iso ? iso.slice(0, 19).replace('T', ' ') : '—';

  return (
    <div className="space-y-6" data-testid="cc-home-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Home</h2>
          <p className="text-muted-foreground text-sm mt-1">System status, what needs you right now, and the 30-day funnel.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="cc-home-refresh-btn"><RefreshCw className="h-4 w-4 mr-2" /> Refresh</Button>
      </div>

      {!data ? <p className="text-muted-foreground text-sm">Loading…</p> : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">System status</CardTitle></CardHeader>
              <CardContent><Badge variant={SYSTEM_STATUS_VARIANT[data.system_status] || 'outline'} data-testid="cc-home-system-status">{data.system_status}</Badge></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">Your role</CardTitle></CardHeader>
              <CardContent><p className="text-xl font-semibold" data-testid="cc-home-role">{ROLE_LABELS[me.user.admin_role]}</p></CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm text-muted-foreground">TEST_MODE</CardTitle></CardHeader>
              <CardContent>
                <Badge variant={me.test_mode ? 'default' : 'destructive'} data-testid="cc-home-test-mode">
                  {me.test_mode ? 'ON — safe' : 'OFF — live destinations'}
                </Badge>
              </CardContent>
            </Card>
          </div>

          <Card data-testid="cc-home-needs-attention">
            <CardHeader className="pb-2"><CardTitle className="text-base">Needs attention</CardTitle>
              <CardDescription>Top {data.needs_attention.length} items, worst first. Nothing here means nothing needs you right now.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-2">
              {data.needs_attention.length === 0 ? (
                <p className="text-muted-foreground text-sm" data-testid="cc-home-needs-attention-empty">Nothing needs you right now.</p>
              ) : data.needs_attention.map((item, i) => {
                const Icon = NEEDS_ATTENTION_ICON[item.type] || AlertTriangle;
                return (
                  <div key={i} className="flex items-center gap-3 border border-border rounded-md p-2.5 text-sm"
                       data-testid={`cc-home-attention-item-${i}`}
                       onClick={() => item.type === 'at_risk_customer' && navigate(`/command-center/customers/${item.id}`)}
                       role={item.type === 'at_risk_customer' ? 'button' : undefined}
                       style={{ cursor: item.type === 'at_risk_customer' ? 'pointer' : 'default' }}>
                    <Badge variant={SEVERITY_COLORS[item.severity] || 'outline'}>{item.severity}</Badge>
                    <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
                    <span className="flex-1 truncate">{item.label}</span>
                    <span className="text-xs text-muted-foreground font-mono shrink-0">{fmt(item.at)}</span>
                  </div>
                );
              })}
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="cc-home-funnel">
              <CardHeader className="pb-2"><CardTitle className="text-base">30-day funnel</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-5 gap-2 text-center">
                {['leads', 'calls', 'registered', 'onboarded', 'activated'].map((k) => (
                  <div key={k}>
                    <p className="text-xl font-semibold" data-testid={`cc-home-funnel-${k}`}>{data.funnel_30d[k]}</p>
                    <p className="text-[10px] text-muted-foreground uppercase">{k}</p>
                  </div>
                ))}
                <p className="col-span-5 text-xs text-muted-foreground mt-1">{data.funnel_30d.conversion_lead_to_activated_pct}% lead → activated</p>
              </CardContent>
            </Card>
            <Card data-testid="cc-home-health-counts">
              <CardHeader className="pb-2"><CardTitle className="text-base">Customer health</CardTitle></CardHeader>
              <CardContent className="flex gap-3 flex-wrap">
                {Object.entries(data.health_counts).map(([state, count]) => (
                  <div key={state} className="flex items-center gap-1.5 text-sm" data-testid={`cc-home-health-count-${state}`}>
                    <span className={`h-2.5 w-2.5 rounded-full ${HEALTH_STATE_COLOR[state]}`} />
                    {state.replace('_', ' ')}: <strong>{count}</strong>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>

          <Card data-testid="cc-home-jobs-per-type">
            <CardHeader className="pb-2"><CardTitle className="text-base">Jobs per type</CardTitle></CardHeader>
            <CardContent>
              <Table>
                <TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Status</TableHead><TableHead>Count</TableHead></TableRow></TableHeader>
                <TableBody>
                  {data.jobs_per_type.slice(0, 15).map((j, i) => (
                    <TableRow key={i}>
                      <TableCell className="text-xs">{j.job_type}</TableCell>
                      <TableCell><Badge variant={j.status === 'DEAD' ? 'destructive' : j.status === 'SUCCEEDED' ? 'default' : 'outline'} className="text-[10px]">{j.status}</Badge></TableCell>
                      <TableCell className="text-xs">{j.count}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card data-testid="cc-home-recent-activity">
            <CardHeader className="pb-2"><CardTitle className="text-base">Recent important activity</CardTitle></CardHeader>
            <CardContent>
              {(!data.recent_activity || data.recent_activity.length === 0) ? (
                <p className="text-muted-foreground text-sm py-4" data-testid="cc-home-recent-activity-empty">Nothing significant yet.</p>
              ) : (
                <ul className="space-y-2">
                  {data.recent_activity.slice(0, 10).map((e, i) => (
                    <li key={e.id || i} className="flex items-center justify-between text-xs border-b border-border/50 pb-1.5 last:border-0"
                        data-testid={`cc-home-recent-activity-row-${i}`}>
                      <span className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px]">{e.category}</Badge>
                        <span>{(e.name || '').replace(/[._]/g, ' ')}</span>
                      </span>
                      <span className="text-muted-foreground">{e.occurred_at ? new Date(e.occurred_at).toLocaleString() : '—'}</span>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}

function JobsPanel({ canManage }) {
  const [jobs, setJobs] = useState([]);
  const [paused, setPaused] = useState([]);
  const [failTimes, setFailTimes] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const q = useDebounced(search);

  const load = useCallback(async () => {
    try {
      const params = { limit: 200 };
      if (q.trim()) params.q = q.trim();
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${API}/jobs`, { params });
      setJobs(res.data.jobs || []);
      setPaused(res.data.paused_job_types || []);
    } catch (e) {
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  }, [q, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const enqueueTestJob = async () => {
    try {
      await axios.post(`${API}/jobs/test-echo`, { fail_times: Number(failTimes) || 0 });
      toast.success('Test job enqueued');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to enqueue'); }
  };

  const tick = async () => {
    try {
      const res = await axios.post(`${API}/jobs/tick`);
      toast.success(`Tick processed ${res.data.processed} job(s)`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Tick failed'); }
  };

  const retryNow = async (jobId) => {
    try {
      await axios.post(`${API}/jobs/${jobId}/retry`);
      toast.success('Job set to retry now');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Retry failed'); }
  };

  const cancelJob = async (jobId) => {
    try {
      await axios.post(`${API}/jobs/${jobId}/cancel`);
      toast.success('Job cancelled');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Cancel failed'); }
  };

  const togglePause = async (jobType) => {
    try {
      if (paused.includes(jobType)) {
        await axios.post(`${API}/jobs/resume`, { job_type: jobType });
        toast.success(`Resumed ${jobType}`);
      } else {
        await axios.post(`${API}/jobs/pause`, { job_type: jobType });
        toast.success(`Paused ${jobType}`);
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const statusColor = { SUCCEEDED: 'default', DEAD: 'destructive', RUNNING: 'secondary', PENDING: 'outline', RETRY_SCHEDULED: 'outline', CANCELLED: 'outline' };

  return (
    <div className="space-y-6" data-testid="cc-jobs-panel">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Jobs</h2>
          <p className="text-muted-foreground text-sm mt-1">Durable retry-with-dead-letter runner. Retry schedule: 1m, 5m, 15m, 1h, 6h, then DEAD.</p>
        </div>
        {canManage && (
          <Button data-testid="cc-jobs-tick-btn" onClick={tick} variant="secondary">
            <RefreshCw className="h-4 w-4 mr-2" /> Force tick
          </Button>
        )}
      </div>

      {canManage && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Enqueue acceptance-test job</CardTitle></CardHeader>
          <CardContent className="flex items-end gap-3">
            <div>
              <Label className="text-xs">fail_times</Label>
              <Input type="number" min="0" value={failTimes} data-testid="cc-jobs-failtimes-input"
                     onChange={(e) => setFailTimes(e.target.value)} className="w-28" />
            </div>
            <Button data-testid="cc-jobs-enqueue-btn" onClick={enqueueTestJob}><Play className="h-4 w-4 mr-2" /> Enqueue test_echo</Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="pt-6 space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <CcSearch value={search} onChange={setSearch} testId="cc-jobs-search" count={jobs.length}
                      placeholder="Search job id, idempotency key, type, error, account/lead id…" />
            <div className="flex gap-1">
              {['all', 'PENDING', 'RETRY_SCHEDULED', 'RUNNING', 'DEAD', 'SUCCEEDED', 'CANCELLED'].map((s) => (
                <button key={s} data-testid={`cc-jobs-filter-${s}`} onClick={() => setStatusFilter(s)}
                        className={`px-2 py-1 text-xs rounded border ${statusFilter === s ? 'border-primary text-foreground bg-muted' : 'border-border text-muted-foreground hover:text-foreground'}`}>
                  {s === 'all' ? 'All' : s}
                </button>
              ))}
            </div>
          </div>
          <Table data-testid="cc-jobs-table">
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead><TableHead>Status</TableHead><TableHead>Attempts</TableHead>
                <TableHead>Run After</TableHead><TableHead>Last Error</TableHead><TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {jobs.map((j) => (
                <TableRow key={j.id} data-testid={`cc-job-row-${j.id}`}>
                  <TableCell className="font-mono text-xs">
                    {j.job_type}
                    <div className="text-[10px] text-muted-foreground truncate max-w-[260px]" title={j.idempotency_key}>{j.id}{j.idempotency_key ? ` · ${j.idempotency_key}` : ''}</div>
                    {canManage && (
                      <Button variant="ghost" size="sm" className="ml-2 h-6 px-1.5" data-testid={`cc-job-pause-${j.id}`}
                              onClick={() => togglePause(j.job_type)}>
                        {paused.includes(j.job_type) ? <PlayCircle className="h-3.5 w-3.5" /> : <PauseCircle className="h-3.5 w-3.5" />}
                      </Button>
                    )}
                  </TableCell>
                  <TableCell><Badge variant={statusColor[j.status] || 'outline'} data-testid={`cc-job-status-${j.id}`}>{j.status}</Badge></TableCell>
                  <TableCell>{j.attempts}/{j.max_attempts}</TableCell>
                  <TableCell className="text-xs font-mono">{j.run_after?.slice(0, 19)}</TableCell>
                  <TableCell className="text-xs max-w-[220px] truncate">{j.last_error || '—'}</TableCell>
                  <TableCell>
                    {canManage && (j.status === 'RETRY_SCHEDULED' || j.status === 'DEAD') && (
                      <Button size="sm" variant="outline" className="mr-1.5" data-testid={`cc-job-retry-${j.id}`} onClick={() => retryNow(j.id)}>Retry now</Button>
                    )}
                    {canManage && (j.status === 'PENDING' || j.status === 'RETRY_SCHEDULED' || j.status === 'DEAD') && (
                      <Button size="sm" variant="ghost" data-testid={`cc-job-cancel-${j.id}`} onClick={() => cancelJob(j.id)}>Cancel</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!loading && jobs.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8" data-testid="cc-jobs-empty">{search || statusFilter !== 'all' ? 'No jobs match this search.' : 'No jobs yet.'}</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function AuditLogPanel() {
  const [entries, setEntries] = useState([]);
  useEffect(() => {
    axios.get(`${API}/audit-log`).then((r) => setEntries(r.data.entries || [])).catch(() => toast.error('Failed to load audit log'));
  }, []);
  return (
    <div className="space-y-6" data-testid="cc-audit-panel">
      <div>
        <h2 className="text-2xl font-semibold">Audit Log</h2>
        <p className="text-muted-foreground text-sm mt-1">Append-only. Every admin action lands here.</p>
      </div>
      <Card>
        <CardContent className="pt-6">
          <Table data-testid="cc-audit-table">
            <TableHeader>
              <TableRow><TableHead>At</TableHead><TableHead>Admin</TableHead><TableHead>Action</TableHead><TableHead>Target</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {entries.map((e) => (
                <TableRow key={e.id} data-testid={`cc-audit-row-${e.id}`}>
                  <TableCell className="text-xs font-mono">{e.at?.slice(0, 19)}</TableCell>
                  <TableCell className="text-xs">{e.role}</TableCell>
                  <TableCell className="text-xs font-mono">{e.action}</TableCell>
                  <TableCell className="text-xs">{e.target_type}/{e.target_id}</TableCell>
                </TableRow>
              ))}
              {entries.length === 0 && (
                <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">No audit entries yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function AdminUsersPanel() {
  const [users, setUsers] = useState([]);
  const [invite, setInvite] = useState({ email: '', name: '', admin_role: 'sales_admin', accepts_leads: false });
  const [inviting, setInviting] = useState(false);
  const [resetFor, setResetFor] = useState(null);
  const [reset, setReset] = useState({ code: '', confirm_user_id: '', reason: '' });

  const submitReset = async () => {
    try {
      const { data } = await axios.post(`${API}/admin-users/${resetFor.id}/reset-mfa`, reset);
      toast.success(`MFA reset — ${resetFor.email} must re-enroll at next login. Notified: ${data.notified.join(', ')}`);
      setResetFor(null); setReset({ code: '', confirm_user_id: '', reason: '' });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'MFA reset failed'); }
  };

  const load = useCallback(() => {
    axios.get(`${API}/admin-users`).then((r) => setUsers(r.data.users || [])).catch(() => toast.error('Failed to load users'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const setRole = async (userId, adminRole) => {
    try {
      await axios.post(`${API}/admin-users/${userId}/role`, { admin_role: adminRole === 'none' ? null : adminRole, is_internal: adminRole !== 'none' });
      toast.success('Role updated');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update role'); }
  };

  const sendInvite = async () => {
    if (!invite.email || !invite.name) { toast.error('Email and name are required'); return; }
    setInviting(true);
    try {
      await axios.post(`${API}/admin-users/invite`, invite);
      toast.success(`Invite sent to ${invite.email}`);
      setInvite({ email: '', name: '', admin_role: 'sales_admin', accepts_leads: false });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to send invite'); } finally { setInviting(false); }
  };

  const resendInvite = async (userId, email) => {
    try {
      await axios.post(`${API}/admin-users/${userId}/resend-invite`);
      toast.success(`Invite resent to ${email}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to resend invite'); }
  };

  return (
    <div className="space-y-6" data-testid="cc-users-panel">
      <div>
        <h2 className="text-2xl font-semibold">Admin Users</h2>
        <p className="text-muted-foreground text-sm mt-1">Owner-only. Promote/demote any account's internal admin role.</p>
      </div>
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Invite a new admin</CardTitle></CardHeader>
        <CardContent className="flex flex-wrap items-end gap-2">
          <div>
            <Label className="text-xs">Email</Label>
            <Input className="h-9 w-56" data-testid="cc-invite-email" value={invite.email}
                   onChange={(e) => setInvite((i) => ({ ...i, email: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Name</Label>
            <Input className="h-9 w-44" data-testid="cc-invite-name" value={invite.name}
                   onChange={(e) => setInvite((i) => ({ ...i, name: e.target.value }))} />
          </div>
          <div>
            <Label className="text-xs">Role</Label>
            <Select value={invite.admin_role} onValueChange={(v) => setInvite((i) => ({ ...i, admin_role: v }))}>
              <SelectTrigger className="w-44 h-9" data-testid="cc-invite-role"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="owner">Owner</SelectItem>
                <SelectItem value="ops_admin">Ops Admin</SelectItem>
                <SelectItem value="sales_admin">Sales Admin</SelectItem>
                <SelectItem value="customer_success">Customer Success</SelectItem>
                <SelectItem value="read_only">Read Only</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <label className="flex items-center gap-2 text-xs pb-2">
            <input type="checkbox" data-testid="cc-invite-accepts-leads" checked={invite.accepts_leads}
                   onChange={(e) => setInvite((i) => ({ ...i, accepts_leads: e.target.checked }))} />
            Accepts sales leads
          </label>
          <Button size="sm" disabled={inviting} data-testid="cc-invite-submit" onClick={sendInvite}>Send invite</Button>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <Table data-testid="cc-users-table">
            <TableHeader>
              <TableRow><TableHead>Email</TableHead><TableHead>Name</TableHead><TableHead>Admin Role</TableHead><TableHead></TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`cc-user-row-${u.id}`}>
                  <TableCell className="text-xs">{u.email}{u.invite_token && <Badge variant="outline" className="ml-2 text-[10px]">invited</Badge>}</TableCell>
                  <TableCell className="text-xs">{u.name}</TableCell>
                  <TableCell>
                    <Select value={u.admin_role || 'none'} onValueChange={(v) => setRole(u.id, v)}>
                      <SelectTrigger className="w-44 h-8 text-xs" data-testid={`cc-user-role-select-${u.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">None (customer)</SelectItem>
                        <SelectItem value="owner">Owner</SelectItem>
                        <SelectItem value="ops_admin">Ops Admin</SelectItem>
                        <SelectItem value="sales_admin">Sales Admin</SelectItem>
                        <SelectItem value="customer_success">Customer Success</SelectItem>
                        <SelectItem value="read_only">Read Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className="space-x-1">
                    {u.invite_token && (
                      <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`cc-user-resend-invite-${u.id}`}
                              onClick={() => resendInvite(u.id, u.email)}>Resend invite</Button>
                    )}
                    {['owner', 'ops_admin'].includes(u.admin_role) && (
                      <Button size="sm" variant="outline" className="h-8 text-xs" data-testid={`cc-user-reset-mfa-${u.id}`}
                              onClick={() => { setResetFor(u); setReset({ code: '', confirm_user_id: '', reason: '' }); }}>
                        Reset MFA{u.mfa_enabled ? '' : u.mfa_enroll_required ? ' (re-enroll pending)' : ' (not enrolled)'}
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
      <Dialog open={!!resetFor} onOpenChange={(open) => !open && setResetFor(null)}>
        <DialogContent data-testid="cc-reset-mfa-dialog">
          <DialogHeader>
            <DialogTitle>Reset MFA for {resetFor?.email}</DialogTitle>
            <DialogDescription>
              Owner-only recovery. You must pass a fresh MFA check, type the target user ID, and give a reason. The action is audited,
              the affected admin and the team inbox are emailed, and they must re-enroll MFA at their next sign-in (MFA is never left off).
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Your current authenticator code</Label>
              <Input className="h-9" data-testid="cc-reset-mfa-code" value={reset.code} onChange={(e) => setReset((r) => ({ ...r, code: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">Type the target user ID to confirm: <span className="font-mono">{resetFor?.id}</span></Label>
              <Input className="h-9 font-mono" data-testid="cc-reset-mfa-confirm-id" value={reset.confirm_user_id} onChange={(e) => setReset((r) => ({ ...r, confirm_user_id: e.target.value }))} />
            </div>
            <div>
              <Label className="text-xs">Reason (required)</Label>
              <Textarea data-testid="cc-reset-mfa-reason" value={reset.reason} onChange={(e) => setReset((r) => ({ ...r, reason: e.target.value }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetFor(null)} data-testid="cc-reset-mfa-cancel">Cancel</Button>
            <Button variant="destructive" onClick={submitReset} data-testid="cc-reset-mfa-submit"
                    disabled={!reset.code || reset.confirm_user_id !== resetFor?.id || reset.reason.trim().length < 5}>Reset MFA</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SecurityMfaPanel() {
  const AUTH = `${BACKEND_URL}/api/auth`;
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState('');
  const [backupCodes, setBackupCodes] = useState(null);
  const [disableOpen, setDisableOpen] = useState(false);
  const [disable, setDisable] = useState({ password: '', code: '' });
  const [disabling, setDisabling] = useState(false);
  const load = useCallback(() => { axios.get(`${AUTH}/mfa/status`).then((r) => setStatus(r.data)).catch(() => {}); }, [AUTH]);
  useEffect(() => { load(); }, [load]);

  const start = async () => {
    try { const { data } = await axios.post(`${AUTH}/mfa/setup`, {}); setSetup(data); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not start enrollment'); }
  };
  const confirm = async () => {
    try {
      const { data } = await axios.post(`${AUTH}/mfa/confirm`, { code });
      setBackupCodes(data.backup_codes); setSetup(null); setCode(''); load();
      toast.success('MFA enabled');
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid code'); }
  };
  const submitDisable = async () => {
    setDisabling(true);
    try {
      const { data } = await axios.post(`${AUTH}/mfa/disable`, { password: status?.has_password ? disable.password : undefined, code: disable.code });
      setDisableOpen(false); setDisable({ password: '', code: '' }); load();
      toast.success(data.re_enroll_required ? 'MFA turned off — you will be asked to enroll again at your next sign-in' : 'MFA turned off');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not turn off MFA'); }
    finally { setDisabling(false); }
  };

  return (
    <div className="space-y-4" data-testid="cc-security-panel">
      <div>
        <h2 className="text-2xl font-semibold">Security — Two-factor authentication</h2>
        <p className="text-muted-foreground text-sm mt-1">Required for Owner and Ops Admin before go-live. Works the same for password and Google sign-in.</p>
      </div>
      <Card>
        <CardContent className="pt-6 space-y-4">
          {!status ? <p className="text-sm text-muted-foreground">Loading…</p> : (
            <div className="flex items-center gap-3 text-sm" data-testid="cc-mfa-status">
              <Badge variant={status.mfa_enabled ? 'default' : 'destructive'}>{status.mfa_enabled ? 'MFA ON' : 'MFA OFF'}</Badge>
              {status.mfa_enabled && <span className="text-muted-foreground">{status.backup_codes_remaining} backup codes remaining</span>}
              {status.mfa_enabled && status.mfa_enrolled_at && <span className="text-muted-foreground" data-testid="cc-mfa-enrolled-at">enrolled {new Date(status.mfa_enrolled_at).toLocaleString()}</span>}
              {status.google_auth && <Badge variant="outline">Google sign-in</Badge>}
            </div>
          )}
          {status && status.mfa_enabled && !backupCodes && (
            <Button variant="outline" data-testid="cc-mfa-disable-btn" onClick={() => setDisableOpen(true)}>Turn off MFA</Button>
          )}
          <Dialog open={disableOpen} onOpenChange={setDisableOpen}>
            <DialogContent data-testid="cc-mfa-disable-dialog">
              <DialogHeader>
                <DialogTitle>Turn off two-factor authentication</DialogTitle>
                <DialogDescription>
                  Confirm with your current authenticator code{status?.has_password ? ' and password' : ''}. Your authenticator and backup codes stop working immediately.
                  {status?.eligible ? ' Because MFA is required for your role, you will be asked to enroll again at your next sign-in.' : ''}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                {status?.has_password && (
                  <div className="space-y-1">
                    <Label>Password</Label>
                    <Input className="h-9" type="password" autoComplete="current-password" data-testid="cc-mfa-disable-password" value={disable.password} onChange={(e) => setDisable((d) => ({ ...d, password: e.target.value }))} />
                  </div>
                )}
                <div className="space-y-1">
                  <Label>6-digit authenticator code</Label>
                  <Input className="h-9" inputMode="numeric" data-testid="cc-mfa-disable-code" value={disable.code} onChange={(e) => setDisable((d) => ({ ...d, code: e.target.value }))} />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setDisableOpen(false)} data-testid="cc-mfa-disable-cancel">Cancel</Button>
                <Button variant="destructive" onClick={submitDisable} data-testid="cc-mfa-disable-submit"
                        disabled={disabling || disable.code.length < 6 || (status?.has_password && !disable.password)}>
                  {disabling ? 'Turning off…' : 'Turn off MFA'}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {status && !status.mfa_enabled && !setup && !backupCodes && (
            <Button data-testid="cc-mfa-enroll-btn" onClick={start}>Enroll authenticator app</Button>
          )}
          {setup && (
            <div className="space-y-3 max-w-sm">
              <img src={setup.qr_code} alt="MFA QR" className="h-44 w-44 border rounded" data-testid="cc-mfa-qr" />
              <p className="text-xs text-muted-foreground break-all">Manual key: <span className="font-mono">{setup.secret}</span></p>
              <div className="flex gap-2">
                <Input className="h-9" placeholder="6-digit code" value={code} onChange={(e) => setCode(e.target.value)} data-testid="cc-mfa-confirm-code" />
                <Button onClick={confirm} data-testid="cc-mfa-confirm-btn">Turn on</Button>
              </div>
            </div>
          )}
          {backupCodes && (
            <div className="space-y-2 max-w-sm" data-testid="cc-mfa-backup-codes">
              <p className="text-sm font-medium">Save these one-time backup codes — shown once.</p>
              <div className="grid grid-cols-2 gap-1 font-mono text-sm bg-muted p-3 rounded">{backupCodes.map((c) => <span key={c}>{c}</span>)}</div>
              <Button variant="outline" size="sm" onClick={() => setBackupCodes(null)} data-testid="cc-mfa-backup-done">Done</Button>
            </div>
          )}
          <p className="text-xs text-muted-foreground">
            Locked out? Use a backup code. Lost those too? Another Owner can reset your MFA under Settings → Admin Users (audited, emailed, forces re-enrollment).
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function CustomersPanel({ canManage, canSeeCS, canManageCS }) {
  const navigate = useNavigate();
  const [subTab, setSubTab] = useState('accounts');
  const [customers, setCustomers] = useState(null);
  const [search, setSearch] = useState('');
  useEffect(() => {
    axios.get(`${API}/customers`).then((r) => setCustomers(r.data.customers))
      .catch(() => toast.error('Failed to load customers'));
  }, []);
  const needle = search.trim().toLowerCase();
  const visible = (customers || []).filter((c) => !needle ||
    [c.account_id, c.org_name, c.owner_name, c.owner_email, c.onboarding_state, c.status, c.plan].some((v) => String(v || '').toLowerCase().includes(needle)));

  const STATE_BADGE = {
    NOT_STARTED: 'outline', IN_PROGRESS: 'secondary', REQUIRED_COMPLETE: 'default',
    FIRST_VALUE: 'default', ACTIVATED: 'default',
  };

  const CUSTOMER_SUBTABS = [
    { key: 'accounts', label: 'Accounts' },
    ...(canSeeCS ? [{ key: 'success', label: 'Success' }] : []),
    { key: 'tickets', label: 'Tickets' },
  ];

  return (
    <div className="space-y-6" data-testid="cc-customers-panel">
      <div>
        <h2 className="text-2xl font-semibold">Customers</h2>
        <p className="text-muted-foreground text-sm mt-1">Customer 360, onboarding status, support tickets & notes per account.</p>
      </div>
      <div className="flex gap-1 border-b border-border">
        {CUSTOMER_SUBTABS.map((s) => (
          <button key={s.key} data-testid={`cc-customers-subtab-${s.key}`}
                  onClick={() => setSubTab(s.key)}
                  className={`px-3 py-2 text-sm border-b-2 -mb-px ${
                    subTab === s.key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {s.label}
          </button>
        ))}
      </div>
      {subTab === 'accounts' && (
        <Card>
          <CardContent className="pt-6 space-y-4">
            <CcSearch value={search} onChange={setSearch} testId="cc-customers-search" count={customers ? visible.length : undefined}
                      placeholder="Search account id, program, owner name, email, onboarding state…" />
            {!customers ? (
              <p className="text-muted-foreground text-sm">Loading…</p>
            ) : customers.length === 0 ? (
              <p className="text-muted-foreground text-sm" data-testid="cc-customers-empty">No accounts yet.</p>
            ) : visible.length === 0 ? (
              <p className="text-muted-foreground text-sm" data-testid="cc-customers-no-match">No accounts match this search.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Program</TableHead><TableHead>Owner</TableHead><TableHead>Onboarding</TableHead>
                    <TableHead>Progress</TableHead><TableHead>First value</TableHead><TableHead>Activated</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visible.map((c) => (
                    <TableRow key={c.account_id} data-testid={`cc-customer-row-${c.account_id}`}
                               className="cursor-pointer hover:bg-muted/50"
                               onClick={() => navigate(`/command-center/customers/${c.account_id}`)}>
                      <TableCell className="text-xs">{c.org_name}{c.is_test_data && <span className="ml-1 text-[9px] uppercase text-amber-500">test</span>}
                        <div className="text-[10px] font-mono text-muted-foreground">{c.account_id}</div></TableCell>
                      <TableCell className="text-xs">{c.owner_name || '—'}<br /><span className="text-muted-foreground">{c.owner_email}</span></TableCell>
                      <TableCell>
                        <Badge variant={STATE_BADGE[c.onboarding_state] || 'outline'} data-testid={`cc-customer-state-${c.account_id}`}>
                          {c.onboarding_state.replace(/_/g, ' ')}
                        </Badge>
                        {c.stalled && <Badge variant="destructive" className="ml-1 text-[10px]">stalled</Badge>}
                      </TableCell>
                      <TableCell className="text-xs">{c.percent_complete}%</TableCell>
                      <TableCell className="text-xs">{c.first_value_at ? '✓' : '—'}</TableCell>
                      <TableCell className="text-xs">{c.activated_at ? '✓' : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}
      {subTab === 'success' && canSeeCS && <CustomerSuccessPanel canSnooze={canManageCS} onOpenAccount={(id) => navigate(`/command-center/customers/${id}`)} />}
      {subTab === 'tickets' && <TicketsPanel canManage={canManage} onOpenAccount={(id) => navigate(`/command-center/customers/${id}`)} />}
    </div>
  );
}

const CS_FILTERS = [
  { key: null, label: 'All' }, { key: 'stalled_onboarding', label: 'Stalled onboarding' },
  { key: 'not_activated', label: 'Not activated' }, { key: 'inactive', label: 'Inactive' },
  { key: 'goal_feature_unused', label: 'Goal feature unused' }, { key: 'open_tickets', label: 'Open tickets' },
  { key: 'technical_problems', label: 'Technical problems' },
];

const CALL_OUTCOMES = [
  { value: 'connected', label: 'Connected' }, { value: 'voicemail', label: 'Left voicemail' },
  { value: 'no_answer', label: 'No answer' },
];

function CustomerSuccessPanel({ canSnooze, onOpenAccount }) {
  const [queue, setQueue] = useState(null);
  const [filter, setFilter] = useState(null);
  const [snoozing, setSnoozing] = useState(null);
  const [snoozeDays, setSnoozeDays] = useState('7');
  const [snoozeReason, setSnoozeReason] = useState('');
  const [callDialogFor, setCallDialogFor] = useState(null);
  const [callOutcome, setCallOutcome] = useState('connected');
  const [callNotes, setCallNotes] = useState('');
  const [reps, setReps] = useState([]);

  const load = useCallback(() => {
    axios.get(`${API}/customer-success`, { params: filter ? { filter } : {} })
      .then((r) => setQueue(r.data.queue)).catch(() => toast.error('Failed to load Customer Success queue'));
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    axios.get(`${API}/customer-success/reps`).then((r) => setReps(r.data.reps)).catch(() => {});
  }, []);

  const snooze = async (accountId) => {
    if (!snoozeReason.trim()) { toast.error('Add a reason first'); return; }
    try {
      const until = new Date(Date.now() + Number(snoozeDays) * 86400000).toISOString();
      await axios.post(`${API}/customers/${accountId}/snooze`, { reason: snoozeReason.trim(), snooze_until: until });
      toast.success(`Snoozed for ${snoozeDays} day(s)`);
      setSnoozing(null); setSnoozeReason(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not snooze'); }
  };

  const submitCall = async () => {
    try {
      await axios.post(`${API}/customers/${callDialogFor}/log-call`, { outcome: callOutcome, notes: callNotes.trim() || null });
      toast.success('Call logged');
      setCallDialogFor(null); setCallOutcome('connected'); setCallNotes(''); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not log call'); }
  };

  const assign = async (accountId, repUserId) => {
    try {
      await axios.post(`${API}/customers/${accountId}/assign`, { rep_user_id: repUserId || null });
      toast.success(repUserId ? 'Assigned' : 'Unassigned');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not assign'); }
  };

  return (
    <div className="space-y-4" data-testid="cc-customer-success-panel">
      <div>
        <h3 className="text-lg font-semibold flex items-center gap-2"><HeartPulse className="h-4 w-4" /> Customer Success queue</h3>
        <p className="text-muted-foreground text-sm mt-1">Every account below AT_RISK/ATTENTION/CRITICAL health, worst first. Snoozed accounts drop off until the date passes.</p>
      </div>
      <div className="flex gap-1 border-b border-border flex-wrap">
        {CS_FILTERS.map((f) => (
          <button key={f.label} data-testid={`cc-cs-filter-${f.key || 'all'}`} onClick={() => setFilter(f.key)}
                  className={`px-3 py-2 text-sm border-b-2 -mb-px ${filter === f.key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {f.label}
          </button>
        ))}
      </div>
      <Card>
        <CardContent className="pt-6">
          {!queue ? <p className="text-muted-foreground text-sm">Loading…</p> : queue.length === 0 ? (
            <p className="text-muted-foreground text-sm" data-testid="cc-cs-empty">No accounts need attention right now.</p>
          ) : (
            <div className="space-y-2">
              {queue.map((q) => (
                <div key={q.account_id} className="border border-border rounded-md p-3" data-testid={`cc-cs-row-${q.account_id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div className="cursor-pointer" onClick={() => onOpenAccount(q.account_id)}>
                      <div className="text-sm font-medium flex items-center gap-1">{q.org_name || 'Unnamed program'} <Badge variant={q.state === 'CRITICAL' ? 'destructive' : 'secondary'}>{q.state}</Badge></div>
                      <p className="text-xs text-muted-foreground">{q.owner_name || '—'} · goal: {q.goals?.primary || 'not set'}</p>
                    </div>
                    {canSnooze && (
                      snoozing === q.account_id ? (
                        <div className="flex gap-1 items-center">
                          <Input value={snoozeReason} onChange={(e) => setSnoozeReason(e.target.value)} placeholder="Reason"
                                 className="h-8 text-xs w-36" data-testid={`cc-cs-snooze-reason-${q.account_id}`} />
                          <Select value={snoozeDays} onValueChange={setSnoozeDays}>
                            <SelectTrigger className="h-8 text-xs w-20" data-testid={`cc-cs-snooze-days-${q.account_id}`}><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="1">1d</SelectItem><SelectItem value="7">7d</SelectItem><SelectItem value="30">30d</SelectItem></SelectContent>
                          </Select>
                          <Button size="sm" className="h-8 text-xs" onClick={() => snooze(q.account_id)} data-testid={`cc-cs-snooze-confirm-${q.account_id}`}>Save</Button>
                        </div>
                      ) : (
                        <div className="flex gap-1 items-center">
                          <Select value={q.assigned_cs_rep_id || 'none'} onValueChange={(v) => assign(q.account_id, v === 'none' ? null : v)}>
                            <SelectTrigger className="h-8 text-xs w-32" data-testid={`cc-cs-assign-select-${q.account_id}`}>
                              <SelectValue placeholder="Unassigned" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Unassigned</SelectItem>
                              {reps.map((r) => <SelectItem key={r.id} value={r.id}>{r.name || r.email}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setCallDialogFor(q.account_id)} data-testid={`cc-cs-logcall-btn-${q.account_id}`}>Log Call</Button>
                          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setSnoozing(q.account_id)} data-testid={`cc-cs-snooze-btn-${q.account_id}`}>Snooze</Button>
                        </div>
                      )
                    )}
                  </div>
                  <ul className="mt-2 space-y-0.5">
                    {q.factors.map((f, i) => <li key={i} className="text-xs text-muted-foreground">• {f.evidence}</li>)}
                  </ul>
                  {q.assigned_cs_rep_name && <p className="mt-1 text-[10px] text-muted-foreground">Assigned to {q.assigned_cs_rep_name}</p>}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <Dialog open={!!callDialogFor} onOpenChange={(open) => !open && setCallDialogFor(null)}>
        <DialogContent data-testid="cc-cs-logcall-dialog">
          <DialogHeader>
            <DialogTitle>Log a call</DialogTitle>
            <DialogDescription>Admin activity only — never counts as the customer's own usage and never changes health rules.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Outcome</Label>
              <Select value={callOutcome} onValueChange={setCallOutcome}>
                <SelectTrigger className="mt-1" data-testid="cc-cs-logcall-outcome"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CALL_OUTCOMES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Notes (optional)</Label>
              <Textarea value={callNotes} onChange={(e) => setCallNotes(e.target.value)} className="mt-1"
                        data-testid="cc-cs-logcall-notes" placeholder="What was discussed…" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCallDialogFor(null)}>Cancel</Button>
            <Button onClick={submitCall} data-testid="cc-cs-logcall-submit">Save call</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ANALYTICS_TABS = [
  { key: 'sales', label: 'Sales' }, { key: 'onboarding', label: 'Onboarding' }, { key: 'product', label: 'Product' },
  { key: 'customer-success', label: 'Customer Success' }, { key: 'operations', label: 'Operations' },
];

function AnalyticsPanel() {
  const [subTab, setSubTab] = useState('sales');
  const [data, setData] = useState(null);

  useEffect(() => {
    setData(null);
    axios.get(`${API}/analytics/${subTab}`).then((r) => setData(r.data)).catch(() => toast.error('Failed to load analytics'));
  }, [subTab]);

  const renderKV = (obj) => (
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
      {Object.entries(obj || {}).map(([k, v]) => (
        <div key={k} className="border border-border rounded-md p-2.5" data-testid={`cc-analytics-metric-${k}`}>
          <p className="text-lg font-semibold">{typeof v === 'object' && v !== null ? JSON.stringify(v) : String(v ?? '—')}</p>
          <p className="text-[10px] text-muted-foreground uppercase">{k.replace(/_/g, ' ')}</p>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6" data-testid="cc-analytics-panel">
      <div>
        <h2 className="text-2xl font-semibold">Analytics</h2>
        <p className="text-muted-foreground text-sm mt-1">Pre-aggregated every 5 minutes — excludes test/internal data.</p>
      </div>
      <div className="flex gap-1 border-b border-border flex-wrap">
        {ANALYTICS_TABS.map((t) => (
          <button key={t.key} data-testid={`cc-analytics-subtab-${t.key}`} onClick={() => setSubTab(t.key)}
                  className={`px-3 py-2 text-sm border-b-2 -mb-px ${subTab === t.key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {t.label}
          </button>
        ))}
      </div>
      <Card>
        <CardContent className="pt-6" data-testid={`cc-analytics-content-${subTab}`}>
          {!data ? <p className="text-muted-foreground text-sm">Loading…</p> : renderKV(data)}
        </CardContent>
      </Card>
    </div>
  );
}

function TicketsPanel({ canManage, onOpenAccount }) {
  const [tickets, setTickets] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [resolvingId, setResolvingId] = useState(null);
  const [resolutionNotes, setResolutionNotes] = useState('');

  const load = () => {
    const params = statusFilter !== 'all' ? { status: statusFilter } : {};
    axios.get(`${API}/tickets`, { params }).then((r) => setTickets(r.data.tickets))
      .catch(() => toast.error('Failed to load tickets'));
  };
  useEffect(() => { load(); }, [statusFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = async (id) => {
    if (!resolutionNotes.trim()) { toast.error('Add resolution notes first'); return; }
    try {
      await axios.post(`${API}/tickets/${id}/resolve`, { resolution_notes: resolutionNotes.trim() });
      toast.success('Ticket resolved');
      setResolvingId(null); setResolutionNotes('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not resolve'); }
  };

  const isOverdue = (t) => t.status !== 'RESOLVED' && t.status !== 'CLOSED' && new Date(t.sla_due_at) < new Date();

  const viewScreenshot = async (id) => {
    try {
      const { data } = await axios.get(`${API}/tickets/${id}/screenshot-url`);
      window.open(data.url, '_blank', 'noopener');
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not open screenshot'); }
  };

  return (
    <div className="space-y-4" data-testid="cc-tickets-panel">
      <div className="flex gap-1 border-b border-border">
        {['all', 'OPEN', 'IN_PROGRESS', 'RESOLVED'].map((s) => (
          <button key={s} data-testid={`cc-tickets-filter-${s}`} onClick={() => setStatusFilter(s)}
                  className={`px-3 py-2 text-sm border-b-2 -mb-px ${statusFilter === s ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {s === 'all' ? 'All' : s.replace('_', ' ')}
          </button>
        ))}
      </div>
      <Card>
        <CardContent className="pt-6">
          {!tickets ? (
            <p className="text-muted-foreground text-sm">Loading…</p>
          ) : tickets.length === 0 ? (
            <p className="text-muted-foreground text-sm" data-testid="cc-tickets-empty">No tickets.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead><TableHead>Category</TableHead><TableHead>Description</TableHead>
                  <TableHead>Priority</TableHead><TableHead>Status</TableHead><TableHead>SLA due</TableHead><TableHead>Screenshot</TableHead>
                  {canManage && <TableHead>Action</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {tickets.map((t) => (
                  <TableRow key={t.id} data-testid={`cc-ticket-row-${t.id}`}>
                    <TableCell className="text-xs capitalize">{t.ticket_type}</TableCell>
                    <TableCell className="text-xs">{t.category}</TableCell>
                    <TableCell className="text-xs max-w-xs truncate cursor-pointer underline" onClick={() => onOpenAccount(t.account_id)}>
                      {t.description}
                    </TableCell>
                    <TableCell><Badge variant={t.priority === 'HIGH' || t.priority === 'CRITICAL' ? 'destructive' : 'outline'}>{t.priority}</Badge></TableCell>
                    <TableCell><Badge variant={t.status === 'RESOLVED' ? 'default' : 'secondary'}>{t.status}</Badge></TableCell>
                    <TableCell className={`text-xs ${isOverdue(t) ? 'text-destructive font-semibold' : ''}`}>
                      {new Date(t.sla_due_at).toLocaleDateString()} {isOverdue(t) && '(overdue)'}
                    </TableCell>
                    <TableCell>
                      {t.screenshot_url ? (
                        <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => viewScreenshot(t.id)} data-testid={`cc-ticket-screenshot-btn-${t.id}`}>View (5 min link)</Button>
                      ) : <span className="text-xs text-muted-foreground">—</span>}
                    </TableCell>
                    {canManage && (
                      <TableCell>
                        {t.status !== 'RESOLVED' && t.status !== 'CLOSED' && (
                          resolvingId === t.id ? (
                            <div className="flex gap-1 items-center">
                              <Input value={resolutionNotes} onChange={(e) => setResolutionNotes(e.target.value)}
                                     placeholder="Resolution notes" className="h-8 text-xs w-40"
                                     data-testid={`cc-ticket-resolution-input-${t.id}`} />
                              <Button size="sm" className="h-8 text-xs" onClick={() => resolve(t.id)} data-testid={`cc-ticket-resolve-confirm-${t.id}`}>Save</Button>
                            </div>
                          ) : (
                            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => setResolvingId(t.id)} data-testid={`cc-ticket-resolve-btn-${t.id}`}>
                              Resolve
                            </Button>
                          )
                        )}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SalesPanel({ canEdit }) {
  const [subTab, setSubTab] = useState('pipeline');
  const SALES_SUBTABS = [
    { key: 'pipeline', label: 'Pipeline', ready: true },
    { key: 'my_follow_ups', label: 'My Follow-ups', ready: canEdit },
  ];
  return (
    <div className="space-y-6" data-testid="cc-sales-panel">
      <div>
        <h2 className="text-2xl font-semibold">Sales</h2>
        <p className="text-muted-foreground text-sm mt-1">Lead pipeline, assignment, scheduling & CRM.</p>
      </div>
      <div className="flex gap-1 border-b border-border">
        {SALES_SUBTABS.map((s) => (
          <button key={s.key} disabled={!s.ready} data-testid={`cc-sales-subtab-${s.key}`}
                  onClick={() => s.ready && setSubTab(s.key)}
                  className={`px-3 py-2 text-sm border-b-2 -mb-px ${!s.ready ? 'text-muted-foreground/40 cursor-not-allowed' :
                    subTab === s.key ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {s.label}
          </button>
        ))}
      </div>
      {subTab === 'pipeline' && <PipelinePanel canEdit={canEdit} />}
      {subTab === 'my_follow_ups' && canEdit && <MyFollowUpsPanel />}
    </div>
  );
}

const LEAD_STATUS_COLORS = {
  LEAD: 'outline', CONTACTED: 'secondary', CALL_SCHEDULED: 'secondary', CALL_COMPLETED: 'secondary',
  QUALIFIED: 'default', FOLLOW_UP: 'secondary', CONVERTED: 'default', NOT_INTERESTED: 'destructive', LOST: 'destructive',
};

function PipelinePanel({ canEdit }) {
  const [leads, setLeads] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [statusConfig, setStatusConfig] = useState([]);
  const [view, setView] = useState('list');
  const [filters, setFilters] = useState({ status: '', state: '', sport: '', source: '', search: '' });
  const [loading, setLoading] = useState(true);
  const [selectedLeadId, setSelectedLeadId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
      const res = await axios.get(`${API}/sales/leads`, { params });
      setLeads(res.data.leads || []);
      setStatuses(res.data.statuses || []);
      setStatusConfig(res.data.status_config || []);
    } catch (e) { toast.error('Failed to load leads'); } finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const labelOf = (key) => statusConfig.find((s) => s.key === key)?.label || key;
  const fmt = (iso) => iso ? iso.slice(0, 16).replace('T', ' ') : '—';

  return (
    <div className="space-y-4" data-testid="cc-pipeline-panel">
      <div className="flex flex-wrap items-end gap-3">
        <Input placeholder="Search name/email/school" value={filters.search} data-testid="cc-leads-search"
               onChange={(e) => setFilters((f) => ({ ...f, search: e.target.value }))} className="w-52" />
        <Select value={filters.status || 'all'} onValueChange={(v) => setFilters((f) => ({ ...f, status: v === 'all' ? '' : v }))}>
          <SelectTrigger className="w-40 h-9" data-testid="cc-leads-status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {statuses.map((s) => <SelectItem key={s} value={s}>{labelOf(s)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input placeholder="State" value={filters.state} data-testid="cc-leads-state-filter"
               onChange={(e) => setFilters((f) => ({ ...f, state: e.target.value }))} className="w-24" />
        <Input placeholder="Sport" value={filters.sport} data-testid="cc-leads-sport-filter"
               onChange={(e) => setFilters((f) => ({ ...f, sport: e.target.value }))} className="w-32" />
        <div className="flex gap-1 ml-auto">
          <Button size="sm" variant={view === 'list' ? 'secondary' : 'ghost'} data-testid="cc-leads-view-list-btn" onClick={() => setView('list')}>
            <List className="h-4 w-4" />
          </Button>
          <Button size="sm" variant={view === 'kanban' ? 'secondary' : 'ghost'} data-testid="cc-leads-view-kanban-btn" onClick={() => setView('kanban')}>
            <LayoutGrid className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {view === 'list' ? (
        <Card>
          <CardContent className="pt-6">
            <Table data-testid="cc-leads-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead><TableHead>School</TableHead><TableHead>Sport</TableHead>
                  <TableHead>State</TableHead><TableHead>Status</TableHead><TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leads.map((l) => (
                  <TableRow key={l.id} data-testid={`cc-lead-row-${l.id}`} className="cursor-pointer hover:bg-muted/40"
                            onClick={() => setSelectedLeadId(l.id)}>
                    <TableCell className="text-xs">{l.first_name} {l.last_name}<br /><span className="text-muted-foreground">{l.email}</span></TableCell>
                    <TableCell className="text-xs">{l.school || '—'}</TableCell>
                    <TableCell className="text-xs">{l.sport || '—'}</TableCell>
                    <TableCell className="text-xs">{l.state || '—'}</TableCell>
                    <TableCell><Badge variant={LEAD_STATUS_COLORS[l.status] || 'outline'} data-testid={`cc-lead-status-${l.id}`}>{labelOf(l.status)}</Badge></TableCell>
                    <TableCell className="text-xs font-mono">{fmt(l.created_at)}</TableCell>
                  </TableRow>
                ))}
                {!loading && leads.length === 0 && (
                  <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No leads yet.</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <div className="flex gap-3 overflow-x-auto pb-2" data-testid="cc-leads-kanban">
          {(statuses.length ? statuses : Object.keys(LEAD_STATUS_COLORS)).map((status) => (
            <div key={status} className="w-64 shrink-0" data-testid={`cc-kanban-column-${status}`}>
              <div className="text-xs font-semibold text-muted-foreground mb-2 flex items-center justify-between">
                <span>{labelOf(status)}</span> <Badge variant="outline" className="text-[10px]">{leads.filter((l) => l.status === status).length}</Badge>
              </div>
              <div className="space-y-2">
                {leads.filter((l) => l.status === status).map((l) => (
                  <Card key={l.id} className="cursor-pointer hover:border-primary/50" data-testid={`cc-kanban-card-${l.id}`}
                        onClick={() => setSelectedLeadId(l.id)}>
                    <CardContent className="p-3">
                      <p className="text-sm font-medium">{l.first_name} {l.last_name}</p>
                      <p className="text-xs text-muted-foreground">{l.school || l.email}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedLeadId && (
        <LeadDetailDialog leadId={selectedLeadId} canEdit={canEdit} statuses={statuses} statusConfig={statusConfig}
                           onClose={() => setSelectedLeadId(null)} onChanged={load} />
      )}
    </div>
  );
}

function LeadDetailDialog({ leadId, canEdit, statuses, statusConfig, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [reps, setReps] = useState([]);
  const [note, setNote] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [lostReason, setLostReason] = useState('');
  const [rescheduleAt, setRescheduleAt] = useState('');

  const load = useCallback(async () => {
    try {
      const [detailRes, repsRes] = await Promise.all([
        axios.get(`${API}/sales/leads/${leadId}`),
        canEdit ? axios.get(`${API}/sales/reps`) : Promise.resolve({ data: { reps: [] } }),
      ]);
      setData(detailRes.data);
      setReps(repsRes.data.reps || []);
      setNewStatus(detailRes.data.lead.status);
    } catch (e) { toast.error('Failed to load lead'); }
  }, [leadId, canEdit]);

  useEffect(() => { load(); }, [load]);

  const baseStatusOf = (key) => statusConfig.find((s) => s.key === key)?.base_status || key;
  const isTerminalReasonRequired = baseStatusOf(newStatus) === 'NOT_INTERESTED' || baseStatusOf(newStatus) === 'LOST';

  const changeStatus = async () => {
    if (isTerminalReasonRequired && !lostReason) {
      toast.error('A reason is required for Not Interested/Lost'); return;
    }
    try {
      await axios.post(`${API}/sales/leads/${leadId}/status`, { status: newStatus, reason: lostReason || undefined });
      toast.success('Status updated'); load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update status'); }
  };

  const reassign = async (repId) => {
    try {
      await axios.post(`${API}/sales/leads/${leadId}/reassign`, { rep_id: repId });
      toast.success('Reassigned'); load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reassign'); }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    try {
      await axios.post(`${API}/sales/leads/${leadId}/notes`, { body: note });
      setNote(''); load();
    } catch (e) { toast.error('Failed to add note'); }
  };

  const convert = async () => {
    try {
      await axios.post(`${API}/sales/leads/${leadId}/convert`);
      toast.success('Lead converted'); load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to convert'); }
  };

  const cancelAppointment = async () => {
    try {
      await axios.post(`${API}/sales/leads/${leadId}/appointment/cancel`);
      toast.success('Call cancelled'); load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to cancel'); }
  };

  const rescheduleAppointment = async () => {
    if (!rescheduleAt) { toast.error('Pick a new date/time first'); return; }
    try {
      await axios.post(`${API}/sales/leads/${leadId}/appointment/reschedule`,
        { starts_at: new Date(rescheduleAt).toISOString() });
      toast.success('Call rescheduled'); setRescheduleAt(''); load(); onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to reschedule'); }
  };

  const chipColor = (status) => status === 'SUCCEEDED' ? 'default' : status === 'DEAD' ? 'destructive' :
    status === 'NOT_STARTED' ? 'outline' : 'secondary';

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="cc-lead-detail-dialog">
        {!data ? (
          <>
            <DialogHeader><DialogTitle>Loading lead…</DialogTitle></DialogHeader>
            <p className="text-sm text-muted-foreground py-8 text-center">Loading…</p>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>{data.lead.first_name} {data.lead.last_name}</DialogTitle>
              <DialogDescription>{data.lead.email} · {data.lead.phone || 'no phone'}</DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-2 gap-3 text-sm">
              <p><span className="text-muted-foreground">School:</span> {data.lead.school || '—'}</p>
              <p><span className="text-muted-foreground">Sport:</span> {data.lead.sport || '—'}</p>
              <p><span className="text-muted-foreground">State:</span> {data.lead.state || '—'}</p>
              <p><span className="text-muted-foreground">Rep:</span> {data.rep?.name || 'Unassigned'}</p>
            </div>
            {data.lead.message && <p className="text-sm bg-muted/40 p-3 rounded-md">{data.lead.message}</p>}

            <div className="flex flex-wrap gap-2">
              {Object.entries(data.automation_chips).map(([k, v]) => (
                <Badge key={k} variant={chipColor(v)} className="text-[10px]" data-testid={`cc-lead-chip-${k}`}>
                  {k.replace(/_/g, ' ')}: {v}
                </Badge>
              ))}
            </div>

            {data.appointment && (
              <div className="text-sm text-muted-foreground space-y-2" data-testid="cc-lead-appointment-block">
                <p className="flex items-center gap-1.5">
                  <CalendarDays className="h-3.5 w-3.5" /> Appointment: {data.appointment.status}
                  {data.appointment.starts_at && ` — ${data.appointment.starts_at.slice(0, 16).replace('T', ' ')}`}
                </p>
                {canEdit && data.appointment.status === 'SCHEDULED' && (
                  <div className="flex items-center gap-2">
                    <input type="datetime-local" data-testid="cc-appointment-reschedule-input" value={rescheduleAt}
                           onChange={(e) => setRescheduleAt(e.target.value)}
                           className="h-8 text-xs bg-background border border-border rounded-md px-2" />
                    <Button size="sm" variant="outline" data-testid="cc-appointment-reschedule-btn" onClick={rescheduleAppointment}>Reschedule</Button>
                    <Button size="sm" variant="destructive" data-testid="cc-appointment-cancel-btn" onClick={cancelAppointment}>Cancel call</Button>
                  </div>
                )}
              </div>
            )}

            {canEdit && (
              <div className="grid grid-cols-2 gap-3 border-t border-border pt-4">
                <div>
                  <Label className="text-xs">Status</Label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger className="h-9 mt-1" data-testid="cc-lead-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{statuses.map((s) => <SelectItem key={s} value={s}>{statusConfig.find((c) => c.key === s)?.label || s}</SelectItem>)}</SelectContent>
                  </Select>
                  {isTerminalReasonRequired && (
                    <Input placeholder="Reason (required)" value={lostReason} data-testid="cc-lead-lost-reason"
                           onChange={(e) => setLostReason(e.target.value)} className="mt-2 h-8 text-xs" />
                  )}
                  <Button size="sm" className="mt-2 w-full" data-testid="cc-lead-status-save-btn" onClick={changeStatus}>Update status</Button>
                </div>
                <div>
                  <Label className="text-xs">Reassign to</Label>
                  <Select value={data.lead.owner_rep_id || ''} onValueChange={reassign}>
                    <SelectTrigger className="h-9 mt-1" data-testid="cc-lead-reassign-select"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                    <SelectContent>{reps.map((r) => <SelectItem key={r.id} value={r.id}>{r.name || r.email}</SelectItem>)}</SelectContent>
                  </Select>
                  <Button size="sm" variant="outline" className="mt-2 w-full" data-testid="cc-lead-convert-btn"
                          disabled={!!data.lead.account_id} onClick={convert}>
                    {data.lead.account_id ? 'Already converted' : 'Convert to account'}
                  </Button>
                </div>
              </div>
            )}

            <div className="border-t border-border pt-4 space-y-2">
              <Label className="text-xs">Activity & notes</Label>
              <div className="max-h-40 overflow-y-auto space-y-1.5" data-testid="cc-lead-activity-list">
                {data.activities.map((a) => (
                  <p key={a.id} className="text-xs text-muted-foreground border-l-2 border-border pl-2">
                    <span className="font-mono">{a.created_at?.slice(0, 16).replace('T', ' ')}</span> — {a.type}: {a.body}
                  </p>
                ))}
                {data.activities.length === 0 && <p className="text-xs text-muted-foreground">No activity yet.</p>}
              </div>
              {canEdit && (
                <div className="flex gap-2">
                  <Textarea rows={2} placeholder="Add a note…" value={note} data-testid="cc-lead-note-input"
                            onChange={(e) => setNote(e.target.value)} className="text-xs" />
                  <Button size="sm" data-testid="cc-lead-note-save-btn" onClick={addNote}>Add</Button>
                </div>
              )}
            </div>
          </>
        )}
        <DialogFooter>
          <Button variant="outline" data-testid="cc-lead-dialog-close-btn" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

const OUTCOME_OPTIONS = [
  { key: 'COMPLETED', label: 'Completed' },
  { key: 'NO_SHOW', label: 'No-show' },
  { key: 'RESCHEDULE', label: 'Reschedule' },
];

function MyFollowUpsPanel() {
  const [tasks, setTasks] = useState([]);
  const load = useCallback(() => {
    axios.get(`${API}/sales/my-follow-ups`).then((r) => setTasks(r.data.tasks || [])).catch(() => toast.error('Failed to load follow-ups'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const complete = async (taskId, outcome) => {
    try {
      await axios.post(`${API}/sales/follow-ups/${taskId}/complete`, outcome ? { outcome } : {});
      toast.success('Marked done'); load();
    } catch (e) { toast.error('Failed to complete'); }
  };

  return (
    <div className="space-y-4" data-testid="cc-my-follow-ups-panel">
      <p className="text-sm text-muted-foreground">Tasks due today or overdue, assigned to you.</p>
      <Card>
        <CardContent className="pt-6 space-y-3">
          {tasks.map((t) => (
            <div key={t.id} className="flex items-center justify-between border-b border-border pb-3 last:border-0" data-testid={`cc-followup-row-${t.id}`}>
              <div>
                <p className="text-sm font-medium">{t.type.replace(/_/g, ' ')}</p>
                <p className="text-xs text-muted-foreground">{t.note}</p>
                <p className="text-xs font-mono text-muted-foreground">Due {t.due_at?.slice(0, 16).replace('T', ' ')}</p>
              </div>
              {t.type === 'log_outcome' ? (
                <div className="flex gap-1.5">
                  {OUTCOME_OPTIONS.map((o) => (
                    <Button key={o.key} size="sm" variant="outline" data-testid={`cc-followup-outcome-${o.key}-${t.id}`}
                            onClick={() => complete(t.id, o.key)}>{o.label}</Button>
                  ))}
                </div>
              ) : (
                <Button size="sm" variant="outline" data-testid={`cc-followup-complete-${t.id}`} onClick={() => complete(t.id)}>Complete</Button>
              )}
            </div>
          ))}
          {tasks.length === 0 && <p className="text-center text-muted-foreground py-8 text-sm">Nothing due — you're all caught up.</p>}
        </CardContent>
      </Card>
    </div>
  );
}

function MyCalendarPanel() {
  const [status, setStatus] = useState(null);

  const load = useCallback(() => {
    axios.get(`${API}/my-calendar`).then((r) => setStatus(r.data)).catch(() => toast.error('Failed to load calendar status'));
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const calendarParam = params.get('calendar');
    if (!calendarParam) return;
    const REASONS = {
      state: 'the connect link expired or was already used — click Connect Google Calendar again',
      denied: 'Google access was declined',
      exchange: 'Google rejected the exchange (OAuth client secret / redirect URI) — contact the Owner',
      missing: 'Google returned no authorization code',
    };
    if (calendarParam === 'connected') toast.success('Google Calendar connected');
    if (calendarParam === 'error') toast.error(`Google Calendar connection failed: ${REASONS[params.get('reason')] || 'please try again'}`);
    window.history.replaceState({}, '', window.location.pathname);
  }, []);

  const connect = async () => {
    try {
      const res = await axios.get(`${API}/my-calendar/connect`);
      window.location.href = res.data.auth_url;
    } catch (e) { toast.error('Failed to start Google connection'); }
  };

  const disconnect = async () => {
    try {
      await axios.post(`${API}/my-calendar/disconnect`);
      toast.success('Disconnected'); load();
    } catch (e) { toast.error('Failed to disconnect'); }
  };

  const toggleAcceptsLeads = async (checked) => {
    try {
      await axios.post(`${API}/my-calendar/accepts-leads`, { accepts_leads: checked });
      setStatus((s) => ({ ...s, accepts_leads: checked }));
    } catch (e) { toast.error('Failed to update'); }
  };

  if (!status) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="cc-my-calendar-panel">
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Google Calendar</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <Badge variant={status.connected ? 'default' : 'outline'} data-testid="cc-calendar-status-badge">{status.status}</Badge>
              <p className="text-xs text-muted-foreground mt-1">Connect your own calendar to receive round-robin sales leads.</p>
            </div>
            {status.connected ? (
              <Button size="sm" variant="outline" data-testid="cc-calendar-disconnect-btn" onClick={disconnect}>
                <Unlink className="h-4 w-4 mr-1.5" /> Disconnect
              </Button>
            ) : (
              <Button size="sm" data-testid="cc-calendar-connect-btn" onClick={connect}>
                <Link2 className="h-4 w-4 mr-1.5" /> Connect Google Calendar
              </Button>
            )}
          </div>
          <div className="flex items-center justify-between border-t border-border pt-4">
            <div>
              <p className="text-sm font-medium">Accept new sales leads</p>
              <p className="text-xs text-muted-foreground">Include me in the round-robin assignment.</p>
            </div>
            <Switch checked={status.accepts_leads} data-testid="cc-accepts-leads-switch" onCheckedChange={toggleAcceptsLeads} />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

const BASE_STATUS_OPTIONS = ['LEAD', 'CONTACTED', 'CALL_SCHEDULED', 'CALL_COMPLETED', 'QUALIFIED', 'FOLLOW_UP', 'CONVERTED', 'NOT_INTERESTED', 'LOST'];

function LeadStatusesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newBase, setNewBase] = useState('FOLLOW_UP');
  const [newLabel, setNewLabel] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/sales/lead-statuses`).then((r) => setRows(r.data.status_config))
      .catch(() => toast.error('Failed to load lead statuses')).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const move = (idx, dir) => {
    const next = [...rows];
    const target = idx + dir;
    if (target < 0 || target >= next.length) return;
    [next[idx], next[target]] = [next[target], next[idx]];
    setRows(next);
  };

  const relabel = (idx, label) => {
    const next = [...rows];
    next[idx] = { ...next[idx], label };
    setRows(next);
  };

  const remove = (idx) => setRows(rows.filter((_, i) => i !== idx));

  const addCustom = () => {
    if (!newLabel.trim()) { toast.error('Enter a label for the new status'); return; }
    const key = newLabel.trim().toUpperCase().replace(/[^A-Z0-9]+/g, '_').replace(/^_+|_+$/g, '');
    if (!key || rows.some((r) => r.key === key)) { toast.error('That status already exists — pick a different label'); return; }
    setRows([...rows, { key, label: newLabel.trim(), base_status: newBase }]);
    setNewLabel('');
  };

  const save = async () => {
    try {
      const res = await axios.put(`${API}/sales/lead-statuses`, rows);
      setRows(res.data.status_config);
      toast.success('Lead statuses saved');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading…</p>;

  return (
    <div className="space-y-4" data-testid="cc-lead-statuses-panel">
      <p className="text-sm text-muted-foreground">
        Rename or reorder any status. The 9 base pipeline stages can't be removed — add a custom status
        mapped to one of them (e.g. "Proposal Sent" → Follow Up) to track a finer sub-stage.
      </p>
      <Card>
        <CardContent className="pt-6 space-y-2">
          {rows.map((row, idx) => {
            const isCustom = !BASE_STATUS_OPTIONS.includes(row.key);
            return (
              <div key={row.key} className="flex items-center gap-2" data-testid={`cc-lead-status-row-${row.key}`}>
                <div className="flex flex-col">
                  <button disabled={idx === 0} data-testid={`cc-lead-status-up-${row.key}`} onClick={() => move(idx, -1)}
                          className="disabled:opacity-20"><ArrowUp className="h-3.5 w-3.5" /></button>
                  <button disabled={idx === rows.length - 1} data-testid={`cc-lead-status-down-${row.key}`} onClick={() => move(idx, 1)}
                          className="disabled:opacity-20"><ArrowDown className="h-3.5 w-3.5" /></button>
                </div>
                <Input value={row.label} data-testid={`cc-lead-status-label-${row.key}`}
                       onChange={(e) => relabel(idx, e.target.value)} className="h-9 flex-1" />
                <Badge variant="outline" className="text-[10px] shrink-0 w-32 justify-center">{row.key}</Badge>
                <Badge variant="secondary" className="text-[10px] shrink-0 w-32 justify-center">base: {row.base_status}</Badge>
                {isCustom && (
                  <Button size="icon" variant="ghost" data-testid={`cc-lead-status-remove-${row.key}`} onClick={() => remove(idx)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </div>
            );
          })}

          <div className="flex items-center gap-2 border-t border-border pt-4 mt-2">
            <Select value={newBase} onValueChange={setNewBase}>
              <SelectTrigger className="w-44 h-9" data-testid="cc-lead-status-new-base"><SelectValue /></SelectTrigger>
              <SelectContent>{BASE_STATUS_OPTIONS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="New custom status label…" value={newLabel} data-testid="cc-lead-status-new-label"
                   onChange={(e) => setNewLabel(e.target.value)} className="h-9 flex-1" />
            <Button size="sm" variant="outline" data-testid="cc-lead-status-add-btn" onClick={addCustom}>
              <Plus className="h-4 w-4 mr-1" /> Add
            </Button>
          </div>

          <Button className="w-full mt-2" data-testid="cc-lead-statuses-save-btn" onClick={save}>Save changes</Button>
        </CardContent>
      </Card>
    </div>
  );
}

function SettingsPanel() {
  const [settings, setSettings] = useState(null);
  const [digest, setDigest] = useState(null);
  const [digestOpen, setDigestOpen] = useState(false);
  const [digestLoading, setDigestLoading] = useState(false);
  const [teamNotifyEmail, setTeamNotifyEmail] = useState('');
  const [thresholdsText, setThresholdsText] = useState('');
  const load = useCallback(() => {
    axios.get(`${API}/settings`).then((r) => {
      setSettings(r.data); setTeamNotifyEmail(r.data.TEAM_NOTIFY_EMAIL || '');
      setThresholdsText(JSON.stringify(r.data.HEALTH_THRESHOLDS || {}, null, 2));
    }).catch(() => toast.error('Failed to load settings'));
  }, []);
  useEffect(() => { load(); }, [load]);

  const previewDigest = async () => {
    setDigestLoading(true);
    try {
      const res = await axios.get(`${API}/sales/digest-preview`);
      setDigest(res.data);
      setDigestOpen(true);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to load digest preview'); }
    finally { setDigestLoading(false); }
  };

  const seedTestData = async () => {
    try {
      const res = await axios.post(`${API}/seed-test-data`);
      toast.success(`Seeded ${res.data.created_job_ids.length} test jobs (is_test_data=true)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Seed failed'); }
  };

  const toggleFailureInjection = async (key, checked) => {
    try {
      const res = await axios.put(`${API}/settings/failure-injection`, { [key]: checked });
      setSettings((s) => ({ ...s, FAILURE_INJECTION: res.data.FAILURE_INJECTION }));
      toast.success(`${key} failure injection ${checked ? 'ON' : 'off'}`);
    } catch (e) { toast.error('Failed to update failure injection'); }
  };

  const saveTeamNotifyEmail = async () => {
    try {
      const res = await axios.put(`${API}/settings/team-notify`, { team_notify_email: teamNotifyEmail });
      setSettings((s) => ({ ...s, TEAM_NOTIFY_EMAIL: res.data.TEAM_NOTIFY_EMAIL }));
      toast.success('Team notification inbox updated');
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
  };

  const toggleTeamNotifyTestData = async (checked) => {
    try {
      const res = await axios.put(`${API}/settings/team-notify`, { send_test_data: checked });
      setSettings((s) => ({ ...s, TEAM_NOTIFY_SEND_TEST_DATA: res.data.TEAM_NOTIFY_SEND_TEST_DATA }));
      toast.success(`Team inbox test-data sends ${checked ? 'ON' : 'off'}`);
    } catch (e) { toast.error('Failed to update'); }
  };

  const testErrorTracking = () => {
    // Phase 5 item 14 QA — deliberately throws so Sentry.ErrorBoundary +
    // the frontend error boundary's /support/client-errors call can both be
    // verified live. Admin-only button, not shown to customers.
    throw new Error('PAC Sentry + client-error test — thrown deliberately from Command Center Settings');
  };

  const saveHealthThresholds = async () => {
    try {
      const parsed = JSON.parse(thresholdsText);
      const res = await axios.put(`${API}/settings/health-thresholds`, { thresholds: parsed });
      setSettings((s) => ({ ...s, HEALTH_THRESHOLDS: res.data.HEALTH_THRESHOLDS }));
      toast.success('Health thresholds saved — next recompute will use them');
    } catch (e) { toast.error(e.response?.data?.detail || 'Invalid JSON or save failed'); }
  };

  const recomputeHealth = async () => {
    try {
      const res = await axios.post(`${API}/health/recompute`);
      toast.success(`Recomputed ${res.data.recomputed} account(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || 'Recompute failed'); }
  };

  return (
    <div className="space-y-6" data-testid="cc-settings-panel">
      <div className="flex items-center justify-between">
        <p className="text-muted-foreground text-sm">Owner-only config module.</p>
        <div className="flex gap-2">
          <Button size="sm" variant="destructive" data-testid="cc-test-error-tracking-btn" onClick={testErrorTracking}>
            Test error tracking
          </Button>
          <Button size="sm" variant="secondary" data-testid="cc-seed-test-data-btn" onClick={seedTestData}>
            <Database className="h-4 w-4 mr-2" /> Seed test data
          </Button>
        </div>
      </div>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Failure injection (QA only)</CardTitle>
          <CardDescription>Force a service to fail on its next attempt, to prove one failing automation never blocks another (R6).</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-6">
          {['email', 'drive', 'calendar', 'ai_provider', 'db', 'auth', 'owner_sms'].map((key) => (
            <label key={key} className="flex items-center gap-2 text-sm" data-testid={`cc-failure-injection-${key}-row`}>
              <Switch checked={!!settings?.FAILURE_INJECTION?.[key]} data-testid={`cc-failure-injection-${key}-switch`}
                      onCheckedChange={(v) => toggleFailureInjection(key, v)} />
              {{ email: 'Email', drive: 'Drive sync', calendar: 'Calendar', ai_provider: 'AI provider', db: 'Database', auth: 'Auth', owner_sms: 'Owner SMS (P1)' }[key]}
            </label>
          ))}
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Team Notification Inbox</CardTitle>
          <CardDescription>Owner amendment A1 — gets ALL incident severities (open/escalate/resolve) + every new support ticket. Separate from the owner's P1/P2 alert above.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-end gap-2">
            <div className="flex-1">
              <Label className="text-xs">Inbox address</Label>
              <Input value={teamNotifyEmail} onChange={(e) => setTeamNotifyEmail(e.target.value)}
                     data-testid="cc-team-notify-email-input" className="h-9 mt-1" />
            </div>
            <Button size="sm" data-testid="cc-team-notify-email-save-btn" onClick={saveTeamNotifyEmail}>Save</Button>
          </div>
          <label className="flex items-center gap-2 text-xs">
            <Switch checked={!!settings?.TEAM_NOTIFY_SEND_TEST_DATA} data-testid="cc-team-notify-send-test-data-switch"
                    onCheckedChange={toggleTeamNotifyTestData} />
            Also send for is_test_data incidents/tickets (off by default)
          </label>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Sales Notification Digest</CardTitle>
          <CardDescription>Sends daily at 8:00am Central. Preview exactly what today's digest would contain right now.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button size="sm" variant="outline" data-testid="cc-digest-preview-btn" onClick={previewDigest} disabled={digestLoading}>
            <Mail className="h-4 w-4 mr-2" /> {digestLoading ? 'Loading…' : 'Preview digest'}
          </Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Customer Health thresholds (Phase 6, spec §11)</CardTitle>
          <CardDescription>Owner-editable day-windows per rule. Off-season (Oct–Mar is in-season) doubles every window automatically. Changes apply on the next recompute.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-2">
          <textarea value={thresholdsText} onChange={(e) => setThresholdsText(e.target.value)} rows={10}
                    className="w-full text-xs font-mono bg-muted/40 p-3 rounded-md border border-border" data-testid="cc-health-thresholds-textarea" />
          <div className="flex gap-2">
            <Button size="sm" data-testid="cc-health-thresholds-save-btn" onClick={saveHealthThresholds}>Save thresholds</Button>
            <Button size="sm" variant="outline" data-testid="cc-health-recompute-btn" onClick={recomputeHealth}>Recompute all now</Button>
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="pt-6">
          <pre className="text-xs font-mono whitespace-pre-wrap bg-muted/40 p-4 rounded-md" data-testid="cc-settings-json">
            {settings ? JSON.stringify(settings, null, 2) : 'Loading...'}
          </pre>
        </CardContent>
      </Card>
      <Dialog open={digestOpen} onOpenChange={setDigestOpen}>
        <DialogContent className="max-w-lg" data-testid="cc-digest-preview-dialog">
          <DialogHeader>
            <DialogTitle>{digest?.subject}</DialogTitle>
            <DialogDescription>Preview only — nothing was sent.</DialogDescription>
          </DialogHeader>
          <div className="text-sm max-h-96 overflow-y-auto border border-border rounded-md p-4"
               data-testid="cc-digest-preview-body" dangerouslySetInnerHTML={{ __html: digest?.body_html || '' }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
